import billingApi from './billingApi'
import axios, { AxiosInstance } from 'axios'
import type { 
  User, 
  Server, 
  AuthResponse,
  LoginCredentials,
  TwoFactorCredentials
} from '@/types/api'
import { apiLogger } from './debug'
import { credentialExchangeFromResponse, isPendingVerificationResponse } from './pendingVerificationSession'
import type { ProjectZomboidVersionSelection } from './projectZomboidVersions'
import type { ProjectZomboidSwitchOperation } from './projectZomboidVersionSwitch'
import authSession from './authSession'
import { normalizeActivityLog } from './activityRegistry'
import type { ServerBillingData } from './serverBillingUtils'
import type { PackageRenewalOffer } from './policyConsent'
import type { BillingEstimate } from './billingEstimate'
import type { OwnerDeleteDisposition } from './backupVaultApi'
import { ServerPowerRequestError } from './serverPowerError'
import { cancellationForServer, getServerCancellations, requireServerCancellationReceipt, ServerCancelledError, type ServerCancellationReceipt } from './serverCancellation'

type ActivityPagination = {
  total?: number
  count?: number
  per_page?: number
  current_page?: number
  total_pages?: number
}

type ListEnvelope<T extends object> = {
  data?: Array<T | { id?: string; attributes?: T }>
  meta?: { pagination?: ActivityPagination; active_count?: number }
}

export interface ProjectZomboidDiscordCapabilities {
  version_id: 'b40' | 'b41' | 'b42' | 'custom'
  mode: 'legacy' | 'b42' | null
  chat: boolean
  logs: boolean
  commands: boolean
  unavailable_reason?: string | null
}

export interface DiscordChannelChoice {
  id: string
  name: string
  position?: number
}

export interface ProjectZomboidDiscordConfiguration {
  guild: DiscordChannelChoice
  chat_channel: DiscordChannelChoice
  log_channel?: DiscordChannelChoice | null
  command_channel?: DiscordChannelChoice | null
  commands_enabled: boolean
  status: 'configured' | 'needs_attention' | string
  last_applied_version?: string | null
  last_error?: string | null
  last_applied_at?: string | null
}

export interface ProjectZomboidDiscordOperation {
  operation_id?: string | null
  action?: 'apply' | 'remove'
  phase: 'idle' | 'queued' | 'stopping' | 'writing_config' | 'starting' | 'completed' | 'failed'
  progress: number
  message?: string | null
  error?: string | null
  active: boolean
  was_running?: boolean | null
  forced_stop?: boolean
  rollback_attempted?: boolean
  rollback_succeeded?: boolean | null
  created_at?: string | null
  updated_at?: string | null
  completed_at?: string | null
}

export interface ProjectZomboidDiscordStatus {
  has_bot: boolean
  integration_type: 'project_zomboid'
  configuration?: ProjectZomboidDiscordConfiguration | null
  capabilities: ProjectZomboidDiscordCapabilities
  operation: ProjectZomboidDiscordOperation
}

export type PalworldSettingSpec = {
  type: 'string' | 'secret' | 'integer' | 'number' | 'boolean' | 'select' | 'multiselect'
  default?: unknown
  min?: number
  max?: number
  max_length?: number
  choices?: string[]
  required?: boolean
  restart_required?: boolean
  category?: string
  description?: string
}

export interface PalworldPreset {
  id: string
  name: string
  description: string
  settings: Record<string, unknown>
}

export interface PalworldConfiguration {
  schema_version: number
  revision: number
  applied_revision: number | null
  pending: boolean
  settings: Record<string, unknown>
  schema: Record<string, PalworldSettingSpec>
  presets?: PalworldPreset[]
}

export interface PalworldOverview {
  state: 'offline' | 'starting' | 'running' | 'stopping' | string
  health: 'healthy' | 'degraded' | 'offline' | string
  api_available: boolean
  community_server?: boolean
  message?: string
  info?: Record<string, unknown>
  metrics?: Record<string, unknown>
  players?: { players?: Array<Record<string, unknown>> } | Array<Record<string, unknown>>
  configuration?: Pick<PalworldConfiguration, 'revision' | 'applied_revision' | 'pending'>
}

export interface PalworldOperation {
  operation_id: string
  operation_type: string
  status: 'queued' | 'running' | 'succeeded' | 'failed'
  phase: string
  progress: number
  message?: string
  error_message?: string
  created_at?: string
  started_at?: string | null
  completed_at?: string | null
  requested_by?: string | null
  prior_power_state?: string | null
  rollback_status?: string | null
}

export interface PalworldImportPreview {
  file: string
  fingerprint: string
  format: 'zip' | 'tar.gz'
  world_root: string
  world_name: string
  source_type: 'dedicated' | 'coop'
  player_count: number
  compressed_bytes: number
  expanded_bytes: number
  has_world_option: boolean
  has_local_data: boolean
  current_world_name?: string
  conflicts: string[]
}

export interface PalworldConversionCandidates {
  candidates: string[]
  world_name: string
  source_guid: string
  detection_source: 'post_import' | 'active_world'
  save_format: 'GVAS' | 'PlZ' | 'PlM'
  conversion_ready: boolean
  conversion_unavailable_reason: string
  conversion_engine: string
  conversion_engine_version: number
}

export interface ProjectZomboidDiscordRequest {
  guild_id: string
  chat_channel_id: string
  log_channel_id?: string | null
  command_channel_id?: string | null
  enable_commands: boolean
  command_confirmation?: string
}

function unwrapListEntry<T extends object>(entry: T | { id?: string; attributes?: T }): T & { id?: string } {
  const wrapper = entry as { id?: string; attributes?: T }
  if (!wrapper.attributes) return entry as T & { id?: string }
  const attributeId = 'id' in wrapper.attributes ? String(wrapper.attributes.id) : undefined
  return { ...wrapper.attributes, id: attributeId || wrapper.id }
}

export interface RustWipeSchedule {
  id: number
  server_uuid: string
  name: string
  wipe_type: 'map' | 'blueprint' | 'full'
  frequency: 'weekly' | 'biweekly' | 'monthly' | 'monthly-weekday' | 'interval'
  interval_days: number
  day_of_week: number
  day_of_month: number
  hour: number
  minute: number
  randomize_seed: boolean
  randomize_size: boolean
  announce_minutes: number[]
  enabled: boolean
  next_run_at: string | null
  last_run_at: string | null
  last_status: string | null
  created_at?: string
  updated_at?: string
  /** IANA timezone for the wall-clock recurrence; execution timestamps are UTC. */
  timezone?: string
}

export type BackupSource =
  | 'manual'
  | 'scheduled'
  | 'before_update'
  | 'before_configuration_change'
  | 'before_restore'
  | 'before_wipe'
  | 'system'

export interface BackupConfigurationSnapshot {
  version: 1
  egg_id: number
  captured_at: string
  variables: Record<string, string>
}

export interface BackupLocation {
  location_id: string
  adapter: 'wings' | 's3' | 'r2'
  storage_config_id?: string | null
  bucket?: string | null
  object_key?: string | null
  node_id?: number | null
  state: 'unverified' | 'available' | 'missing' | 'unavailable'
  verified_at?: string | null
  cleanup_state: 'none' | 'pending' | 'deleted'
}

export interface ManagedBackup {
  backup_uuid: string
  server_uuid: string
  name: string
  source: BackupSource
  status: 'preparing' | 'creating' | 'uploading' | 'verifying' | 'available' | 'restoring' | 'failed' | 'deleting' | 'unavailable' | 'missing'
  bytes: number
  checksum?: string | null
  is_locked: boolean
  failure_message?: string | null
  configuration_snapshot?: BackupConfigurationSnapshot | null
  expires_at?: string | null
  created_at: string
  locations?: BackupLocation[]
  off_node?: boolean
  retained_server?: boolean
  server_state?: 'active' | 'retained' | 'unknown'
  server_name?: string | null
  application_name?: string | null
  egg_id?: number | null
  snapshot_available?: boolean
  deletion_deadline?: string | null
  first_verified_at?: string | null
  estimated_monthly_cost?: number | null
  currency?: string | null
  retention_extension?: string | null
  completed_at?: string | null
}

export interface BackupOperation {
  operation_id: string
  server_uuid: string
  backup_uuid?: string | null
  safety_backup_uuid?: string | null
  operation_type: 'create' | 'restore' | 'delete'
  source: BackupSource
  status: 'queued' | 'running' | 'completed' | 'failed'
  phase: string
  progress: number
  message?: string | null
  error_message?: string | null
  started_at: string
  completed_at?: string | null
}

export interface BackupSchedule {
  server_uuid: string
  enabled: boolean
  frequency: 'daily' | 'weekly'
  time_of_day: string
  timezone: string
  days_of_week: number[]
  retention_mode: 'keep_all' | 'days' | 'count'
  retention_value?: number | null
  next_run_at?: string | null
  last_run_at?: string | null
  last_success_at?: string | null
  last_status?: string | null
  last_error?: string | null
}

export interface BackupManagement {
  server_uuid: string
  backups: ManagedBackup[]
  counts: { total: number; manual: number; automatic: number }
  usage: {
    stored_bytes: number
    gib_hours_this_month: number
    estimated_monthly_cost: number | null
    currency: string | null
    unit_rate: number | null
    billing_status: 'estimate_only' | 'billable' | 'unavailable'
    subscription_item_id?: string | null
  }
  last_successful_backup?: ManagedBackup | null
  schedule?: BackupSchedule | null
  operations: BackupOperation[]
}

export class SessionRecoveryUnavailableError extends Error {
  constructor() {
    super('Metrly could not restore this session yet.')
    this.name = 'SessionRecoveryUnavailableError'
  }
}

class ApiClient {
  private client: AxiosInstance
  private loggingOut = false
  private billingEstimateRequest: { session: string | null; promise: Promise<BillingEstimate> } | null = null
  private clientApiKey: string | null = null
  private panelRecovery: Promise<{ status: 'recovered'; user: User | null } | { status: 'expired' | 'unavailable' }> | null = null
  // Persist the bearer token for refresh survival (session-scoped)
  private static STORAGE_KEY = 'peltast_client_api_key'
  private static STORAGE_TS_KEY = 'peltast_client_api_key_ts'
  // TTL for persisted key (ms). Keep short to limit exposure; refreshed on login.
  private static STORAGE_TTL = 30 * 24 * 60 * 60 * 1000 // 30 days
  private static PANEL_RECOVERY_LOCK = 'metrly-panel-session-recovery'
  // Simple in-memory caches to reduce duplicate requests and improve perceived performance
  private serversCache: { ts: number; data: import('@/types').Server[]; promise?: Promise<import('@/types').Server[]> } | null = null
  private resourcesCache: Map<string, { ts: number; data: any; promise?: Promise<any> }> = new Map()
  // Cookie helpers no longer used; bearer tokens are preferred.

  // Prefer meta csrf token, fall back to XSRF-TOKEN cookie
  // (Deprecated) We now always re-prime CSRF from server HTML and XSRF-TOKEN cookie when needed.

  // Note: HttpOnly cookies are not readable from JS; do not gate logic on cookie presence.

  constructor() {
    this.client = axios.create({
      baseURL: '', // Use relative URLs to go through nginx proxy
      timeout: 10000,
      headers: {
        'Accept': 'application/json',
  'Content-Type': 'application/json',
  'X-Requested-With': 'XMLHttpRequest',
      },
      withCredentials: false,
    })

    // Always attach the Panel Client API bearer token for Panel client endpoints
    this.client.interceptors.request.use((config) => {
      try {
        const url = String(config.url || '')
        // Only set Authorization for Panel Client API calls
        if (url.startsWith('/panel/api/client') || url.includes('/panel/api/client/')) {
          config.headers = config.headers || {}
          const clientApiKey = this.getClientApiKey()
          if (clientApiKey) {
            ;(config.headers as any).Authorization = `Bearer ${clientApiKey}`
          }
          // Ensure we never rely on cookies for these requests
          config.withCredentials = false
        }
      } catch {}
      return config
    })


    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          const original = error.config as typeof error.config & {
            _hoardRetry?: boolean
            _panelRecoveryCount?: number
          }
          const url = String(original?.url || '')

          if (url.startsWith('/panel/api/client')) {
            const recoveryCount = original._panelRecoveryCount || 0
            if (recoveryCount < 2) {
              original._panelRecoveryCount = recoveryCount + 1
              const authorization = original.headers?.Authorization
              const failedPanelKey = typeof authorization === 'string'
                ? authorization.replace(/^Bearer\s+/i, '')
                : this.getClientApiKey()
              const recovery = await this.recoverPanelSession(
                recoveryCount === 1,
                failedPanelKey,
              )
              if (recovery.status === 'recovered') {
                original.headers = original.headers || {}
                ;(original.headers as any).Authorization = `Bearer ${this.getClientApiKey()}`
                return this.client(original)
              }
              if (recovery.status === 'expired') authSession.expireSession()
            }
          } else if (!original?._hoardRetry && original?.headers?.Authorization) {
            original._hoardRetry = true
            const failedAccessToken = authSession.getAccessToken()
            const refreshed = await authSession.refreshAccessToken({ failedAccessToken })
            if (refreshed.status === 'available') {
              original.headers.Authorization = `Bearer ${refreshed.accessToken}`
              return this.client(original)
            }
            if (refreshed.status === 'expired') authSession.expireSession()
          }
        }
  // Don't emit global toasts here; let callers render inline errors.
        
  return Promise.reject(error)
      }
    )

    // Attempt to restore a valid client API key from sessionStorage on load
    this.restoreClientKeyFromStorage()
  }

  // Helper: attach Hoard JWT (issued by Hoard on login) when calling Hoard APIs
  private hoardJwtConfig(extra?: any): any {
    try {
      const token = authSession.getAccessToken()
      const headers = {
        ...(extra?.headers || {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      }
      return { ...(extra || {}), headers }
    } catch {
      return extra || {}
    }
  }

  // Extracts the payload shape returned by Pterodactyl (object/attributes) or { data }
  private extractData<T = any>(raw: any): T | null {
    try {
      if (!raw) return null
      // Only accept the Pterodactyl user payload shape
      if (raw.object === 'user' && raw.attributes && typeof raw.attributes === 'object') {
        const attrs = raw.attributes
        // Panel uses 'admin' but our types use 'root_admin' - normalize it
        if ('admin' in attrs && !('root_admin' in attrs)) {
          attrs.root_admin = attrs.admin
        }
        return attrs as T
      }
      if (raw.data && typeof raw.data === 'object') {
        const d = raw.data
        if (d.object === 'user' && d.attributes && typeof d.attributes === 'object') {
          const attrs = d.attributes
          // Panel uses 'admin' but our types use 'root_admin' - normalize it
          if ('admin' in attrs && !('root_admin' in attrs)) {
            attrs.root_admin = attrs.admin
          }
          return attrs as T
        }
      }
      return null
    } catch {
      return null
    }
  }

  private clearCookie(name: string) {
    try {
      // Clear host-only cookie
      document.cookie = `${name}=; Max-Age=0; path=/`
      // Clear domain cookie variant
      const host = window.location.hostname
      document.cookie = `${name}=; Max-Age=0; path=/; domain=${host}`
    } catch {
      // ignore
    }
  }

  private clearAuth() {
    this.clientApiKey = null
    // Clear persisted token state
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(ApiClient.STORAGE_KEY)
        window.localStorage.removeItem(ApiClient.STORAGE_TS_KEY)
        // Also clear legacy sessionStorage keys from prior builds
        window.sessionStorage.removeItem(ApiClient.STORAGE_KEY)
        window.sessionStorage.removeItem(ApiClient.STORAGE_TS_KEY)
      }
    } catch {}
    // Best-effort client cleanup to avoid stale cookies across logouts/logins
  this.clearCookie('peltast_session')
    this.clearCookie('XSRF-TOKEN')
    // Aggressively clear Laravel remember cookies (e.g., remember_web_*)
    try {
      const cookies = document.cookie.split(';')
      const host = window.location.hostname
      for (const c of cookies) {
        const [rawName] = c.split('=')
        const name = rawName?.trim()
        if (!name) continue
    const lower = name.toLowerCase()
    if (lower.startsWith('remember') || lower.includes('session') || lower === 'laravel_session' || lower === 'pterodactyl_session') {
          document.cookie = `${name}=; Max-Age=0; path=/`
          document.cookie = `${name}=; Max-Age=0; path=/; domain=${host}`
        }
      }
    } catch {
      // ignore
    }
  }

  // Silent rehydrate removed; sessions require explicit login each visit.

  // Authentication - Now using Hoard for full control
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
      try {
        // Call Hoard's login endpoint instead of Panel
        // Hoard now returns a per-user Pterodactyl Client API key
        const response = await this.client.post('/hoard-api/auth/login', {
          username: credentials.username,
          password: credentials.password,
        }, {
          validateStatus: (s) => s < 500,
        })

        if (response.status === 200 && isPendingVerificationResponse(response.data)) {
          this.clearPanelCredentials()
          return response.data as AuthResponse
        }

        if (response.status === 200 && response.data.success) {
          // Store client API key for future requests
          if (response.data.client_api_key) {
            this.setClientApiKey(response.data.client_api_key as string)
          }
          if (response.data.access_token) {
            authSession.storeCredentials(response.data.access_token, response.data.refresh_token)
          }
          return {
            complete: response.data.complete || true,
            intended: response.data.intended || '/',
            confirmation_token: '',
            user: response.data.user,
            client_api_key: this.clientApiKey || undefined,
            access_token: response.data.access_token,
            refresh_token: response.data.refresh_token,
            force_password_change: !!response.data.force_password_change,
            requires_payment_method: !!response.data.requires_payment_method,
            package_card_optional: !!response.data.package_card_optional,
            requires_email_verification: !!response.data.requires_email_verification,
          }
        }

        // Check if 2FA is required - throw this as an error so the UI can catch it
        if (response.status === 200 && response.data.requires_2fa) {
          // Create a structured error that includes the 2FA data
          const twoFactorError = new Error('2FA Required')
          ;(twoFactorError as any).response = {
            status: 200,
            data: {
              requires_2fa: true,
              confirmation_token: response.data.confirmation_token
            }
          }
          throw twoFactorError
        }

        // Login failed
        const errorMessage = response.data?.error || 'Invalid email or password'
        throw new Error(errorMessage)
      } catch (error: any) {
        // Re-throw 2FA errors as-is
        if (error.message === '2FA Required' && error.response) {
          throw error
        }
        if (error.response) {
          const errorMessage = error.response.data?.error || 'Invalid email or password'
          throw new Error(errorMessage)
        }
        throw error
      }
  }

  // Persist and set the current client API key
  private setClientApiKey(token: string) {
    this.clientApiKey = token
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(ApiClient.STORAGE_KEY, token)
        window.localStorage.setItem(ApiClient.STORAGE_TS_KEY, String(Date.now()))
      }
    } catch {}
  }

  private getClientApiKey(): string | null {
    try {
      const stored = typeof window === 'undefined'
        ? null
        : window.localStorage.getItem(ApiClient.STORAGE_KEY)
      if (stored !== this.clientApiKey) this.clientApiKey = stored
    } catch {}
    return this.clientApiKey
  }

  clearPanelCredentials(): void {
    this.clearAuth()
  }

  acceptCredentialExchange(value: unknown): AuthResponse | null {
    const exchange = credentialExchangeFromResponse(value)
    if (!exchange) return null
    this.setClientApiKey(exchange.client_api_key)
    authSession.storeCredentials(exchange.access_token, exchange.refresh_token)
    return {
      complete: true,
      intended: exchange.intended || '/',
      user: exchange.user as User | undefined,
      client_api_key: exchange.client_api_key,
    }
  }

  // Restore a valid client API key from storage if not expired
  private restoreClientKeyFromStorage() {
    try {
      if (typeof window === 'undefined') return
      const token = window.localStorage.getItem(ApiClient.STORAGE_KEY)
      const tsRaw = window.localStorage.getItem(ApiClient.STORAGE_TS_KEY)
      const ts = tsRaw ? parseInt(tsRaw, 10) : 0
      if (!token || !ts || Number.isNaN(ts)) return
      if (Date.now() - ts > ApiClient.STORAGE_TTL) {
        // Expired; clear it
        window.localStorage.removeItem(ApiClient.STORAGE_KEY)
        window.localStorage.removeItem(ApiClient.STORAGE_TS_KEY)
        return
      }
      this.clientApiKey = token
    } catch {}
  }

  // Legacy login response handler removed (cookie-based auth deprecated).

  async requestPasswordReset(email: string): Promise<void> {
    // Call Hoard's password reset endpoint instead of Panel's
    // Hoard will:
    // 1. Generate a token and hash it
    // 2. Insert into Panel's password_resets table
    // 3. Send our branded email with the plain token
    await this.client.post('/hoard-api/email/request-password-reset', {
      email
    })
  }

  async validateResetToken(email: string, token: string): Promise<{ valid: boolean; message: string }> {
    // Call Hoard to validate the reset token before showing the form
    const response = await this.client.post<{ valid: boolean; message: string }>(
      '/hoard-api/email/validate-reset-token',
      { email, token }
    )
    return response.data
  }

  async invalidateResetToken(email: string): Promise<void> {
    // Call Hoard to delete the reset token from Panel's database
    // This ensures the token cannot be reused after a successful password reset
    await this.client.post('/hoard-api/email/invalidate-reset-token', {
      email
    })
  }

  async resetPassword(email: string, token: string, password: string, passwordConfirmation: string): Promise<void> {
    const response = await this.client.post('/hoard-api/email/reset-password', {
      email,
      token,
      password,
      password_confirmation: passwordConfirmation,
    })

    if (!response.data?.success) {
      throw new Error(response.data?.message || 'Password reset failed')
    }
  }

  async loginCheckpoint(credentials: TwoFactorCredentials): Promise<AuthResponse> {
    const response = await this.client.post<AuthResponse>('/api/auth/login/checkpoint', credentials)
    if (response.data.client_api_key) {
      this.setClientApiKey(response.data.client_api_key)
    }
    if (response.data.access_token) {
      authSession.storeCredentials(response.data.access_token, response.data.refresh_token)
    }

    if (response.data.complete) {
      // Hoard returns user + client_api_key directly; hydrate Panel account data
      try { await this.initializeAuth() } catch {}
    }

    return response.data
  }

  async logout(): Promise<void> {
  if (this.loggingOut) return
  this.loggingOut = true
    try {
      // Best-effort cleanup of ephemeral Hoard-created Panel API keys before logging out
      try {
        await this.cleanupHoardClientApiKeys(5000)
      } catch (e) {
        // Non-fatal: logout should proceed regardless of cleanup outcome
        apiLogger.warn('API key cleanup skipped or failed:', e)
      }

      // Use Hoard's logout endpoint - it handles session deletion and cookie expiry
      try {
        await fetch('/hoard-api/auth/logout', { 
          method: 'POST', 
          credentials: 'include' 
        })
      } catch (e) {
        apiLogger.warn('Logout request failed:', e)
      }
    } catch (e) {
      // ignore errors; still clear client-side state
    } finally {
      this.clearAuth()
      // Extra client-side domain/path variants purge (belt and suspenders)
      try {
        const host = window.location.hostname
        const parts = host.split('.')
        const domains: string[] = []
        for (let i = 0; i < parts.length - 1; i++) domains.push(parts.slice(i).join('.'))
        const names = ['peltast_session','laravel_session','pterodactyl_session','XSRF-TOKEN','remember_me','remember_web']
        const paths = ['/', '/panel', '/panel/']
        for (const n of names) {
          for (const d of domains) {
            for (const p of paths) {
              document.cookie = `${n}=; Max-Age=0; path=${p}; domain=${d}`
            }
          }
        }
      } catch {}
      this.loggingOut = false
      try {
        // Force a hard reload to ensure no SPA cached state; navigate to login.
        if (typeof window !== 'undefined') {
          window.location.replace('/login')
        }
      } catch {}
    }
  }


  /**
   * Deletes any Panel Client API keys created by Hoard for this user.
   * Runs best-effort with a timeout; ignores errors so logout isn’t blocked.
   * Note: Uses the current bearer token to authorize deletions.
   */
  private async cleanupHoardClientApiKeys(timeoutMs: number = 5000): Promise<void> {
    if (!this.clientApiKey) return

    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), Math.max(1000, timeoutMs))
    try {
      // List keys
      const { data } = await this.client.get('/panel/api/client/account/api-keys', {
        headers: { Authorization: `Bearer ${this.clientApiKey}` },
        signal: controller.signal as any,
        validateStatus: (s) => s < 500,
      })

      const keys = Array.isArray(data?.data) ? data.data : []
      if (!keys.length) return

      // Identify keys created by Hoard
      const hoardKeys = keys
        .map((d: any) => d?.attributes || {})
        .filter((a: any) => typeof a?.description === 'string' && a.description.toLowerCase().includes('hoard client api key'))

      if (!hoardKeys.length) return

      // Sort so we delete older/unused keys first; try current token last if we accidentally include it
      hoardKeys.sort((a: any, b: any) => {
        const ta = a.last_used_at ? Date.parse(a.last_used_at) : 0
        const tb = b.last_used_at ? Date.parse(b.last_used_at) : 0
        return ta - tb
      })

      for (const k of hoardKeys) {
        const id = k.identifier
        if (!id) continue
        try {
          await this.client.delete(`/panel/api/client/account/api-keys/${id}`, {
            headers: { Authorization: `Bearer ${this.clientApiKey}` },
            signal: controller.signal as any,
            validateStatus: (s) => s < 500,
          })
        } catch (e) {
          // If the token deletes itself mid-flight, subsequent requests may 401/abort. That's acceptable.
          // Continue attempting remaining deletions; ignore errors.
        }
      }
    } catch (e) {
      // Ignore cleanup errors
    } finally {
      clearTimeout(timer)
    }
  }

  async initializeAuth(): Promise<User | null> {
    try {
      // If we have a client API key, use it to fetch account info
      const clientApiKey = this.getClientApiKey()
      const headers = clientApiKey ? { Authorization: `Bearer ${clientApiKey}` } : undefined
      const response = await this.client.get('/panel/api/client/account', {
        headers,
        validateStatus: (s) => s < 500,
      })

      if (response.status === 200) {
        const extracted = this.extractData<User>((response as any).data)
        if (extracted && clientApiKey) {
          try {
            const { data: status } = await this.client.get('/api/account/two-factor/status', {
              headers: { Authorization: `Bearer ${clientApiKey}` },
            })
            if (typeof status?.use_totp === 'boolean') extracted.use_totp = status.use_totp
            if (typeof status?.root_admin === 'boolean') extracted.root_admin = status.root_admin
            if (status?.role === 'user' || status?.role === 'sre' || status?.role === 'admin') extracted.role = status.role
          } catch { /* Hoard rehydrate remains the fallback if Panel credentials later fail. */ }
        }
        if (extracted?.role) return extracted
        if (extracted) {
          const recovery = await this.recoverPanelSession(false)
          if (recovery.status === 'recovered' && recovery.user) return recovery.user
          return extracted
        }
      }

      // Client API key missing or rejected — try rehydrating via Hoard JWT
      const recovery = await this.recoverPanelSession(false)
      if (recovery.status === 'expired') authSession.expireSession()
      if (recovery.status === 'unavailable') throw new SessionRecoveryUnavailableError()
      return recovery.status === 'recovered' ? recovery.user : null
    } catch (error) {
      if (error instanceof SessionRecoveryUnavailableError) throw error
      // Last-resort rehydration attempt
      try {
        const recovery = await this.recoverPanelSession(false)
        if (recovery.status === 'expired') authSession.expireSession()
        if (recovery.status === 'unavailable') throw new SessionRecoveryUnavailableError()
        return recovery.status === 'recovered' ? recovery.user : null
      } catch (recoveryError) {
        if (recoveryError instanceof SessionRecoveryUnavailableError) throw recoveryError
      }
      return null
    }
  }

  /**
   * Use the Hoard access token (or refresh it first) to get a fresh
   * Pterodactyl Client API key from Hoard's /auth/rehydrate endpoint.
   */
  private async recoverPanelSession(forceRotate: boolean, failedPanelKey?: string | null): Promise<{ status: 'recovered'; user: User | null } | { status: 'expired' | 'unavailable' }> {
    if (this.panelRecovery) return this.panelRecovery
    type PanelRecoveryResult = { status: 'recovered'; user: User | null } | { status: 'expired' | 'unavailable' }
    const recoveryPromise = authSession.runExclusive(ApiClient.PANEL_RECOVERY_LOCK, async (): Promise<PanelRecoveryResult> => {
      // Another tab may have repaired or rotated the key while this request
      // waited. Retry with that key instead of rotating it again.
      const latestPanelKey = this.getClientApiKey()
      if (failedPanelKey && latestPanelKey && latestPanelKey !== failedPanelKey) {
        return { status: 'recovered', user: null }
      }

      const tokenResult = await authSession.getValidAccessToken()
      if (tokenResult.status !== 'available') return tokenResult

      const rehydrate = (accessToken: string) => axios.post('/hoard-api/auth/rehydrate', {
        rotate_client_api_key: forceRotate,
      }, {
        headers: { Authorization: `Bearer ${accessToken}` },
        validateStatus: status => status < 500,
      })

      try {
        let response = await rehydrate(tokenResult.accessToken)
        if ([401, 403].includes(response.status)) {
          const refreshed = await authSession.refreshAccessToken({
            failedAccessToken: tokenResult.accessToken,
          })
          if (refreshed.status !== 'available') return refreshed
          response = await rehydrate(refreshed.accessToken)
        }

        if ([401, 403].includes(response.status)) return { status: 'expired' }
        if (response.status !== 200 || !response.data?.client_api_key) {
          return { status: 'unavailable' }
        }
        this.setClientApiKey(response.data.client_api_key)
        return { status: 'recovered', user: response.data.user ?? null }
      } catch {
        return { status: 'unavailable' }
      }
    })
    const activeRecovery = recoveryPromise.finally(() => {
      this.panelRecovery = null
    })
    this.panelRecovery = activeRecovery
    return activeRecovery
  }

  /**
   * Attempt to refresh the Hoard access token using the stored refresh token.
   * Returns true if a new access token was stored successfully.
   */
  private async refreshHoardToken(): Promise<boolean> {
    const result = await authSession.refreshAccessToken({
      failedAccessToken: authSession.getAccessToken(),
    })
    return result.status === 'available'
  }

  // User account
  async getAccount(): Promise<User> {
    const response = await this.client.get('/panel/api/client/account', {
      headers: this.clientApiKey ? { Authorization: `Bearer ${this.clientApiKey}` } : undefined,
    })
    const user = this.extractData<User>(response.data) as User
    
    // Enrich with real 2FA status and root_admin from Hoard (Panel Client API doesn't include these)
    if (this.clientApiKey) {
      try {
        const { data: twoFactorStatus } = await this.client.get('/api/account/two-factor/status', {
          headers: { Authorization: `Bearer ${this.clientApiKey}` }
        })
        if (twoFactorStatus && typeof twoFactorStatus.use_totp === 'boolean') {
          user.use_totp = twoFactorStatus.use_totp
        }
        if (typeof twoFactorStatus.root_admin === 'boolean') {
          user.root_admin = twoFactorStatus.root_admin
        }
        if (twoFactorStatus.role === 'user' || twoFactorStatus.role === 'sre' || twoFactorStatus.role === 'admin') {
          user.role = twoFactorStatus.role
        }
      } catch {
        // Fail silently - use whatever Panel returned
      }
    }
    
    return user
  }

  async updateEmail(email: string, password: string): Promise<void> {
    await this.client.put('/panel/api/client/account/email', { email, password })
  }

  async updatePassword(currentPassword: string, password: string): Promise<void> {
  await this.client.put('/panel/api/client/account/password', {
      current_password: currentPassword,
      password,
      password_confirmation: password,
    })
  }

  async updateAccountName(first_name: string, last_name: string): Promise<void> {
    // Hoard alias requires the Panel Client API key in Authorization for user resolution
    if (!this.clientApiKey) throw new Error('Not authenticated')
    await this.client.put('/hoard-api/account/name', { first_name, last_name }, {
      headers: { Authorization: `Bearer ${this.clientApiKey}` },
    })
  }

  // Servers
  async getServers(): Promise<Server[]> {
    // TEMPORARY: Clear cache to force fresh fetch with new include parameters
    // TODO: Make cache key include the 'include' parameter to properly cache different queries
    this.serversCache = null
    
    // Cache/dedupe: return cached within TTL or share in-flight promise
    const TTL = 15_000 // 15s; fast refresh but avoids double-loading on initial screens
    const now = Date.now()
    if (this.serversCache) {
      const { ts, data, promise } = this.serversCache
      if (promise) return promise
      if (now - ts < TTL) return data
    }

    const p = (async () => {
      // Follow Pterodactyl's pattern: get basic server list with nest and allocations information
      apiLogger.debug('Making request to /panel/api/client with nest and allocations inclusion')
      const [response, cancellations] = await Promise.all([this.client.get<any>('/panel/api/client', {
        params: { per_page: 100, include: 'nest,allocations' },
        headers: this.clientApiKey ? { Authorization: `Bearer ${this.clientApiKey}` } : undefined,
      }), getServerCancellations()])

      apiLogger.debug('Raw response received:', response.data)
      const list: any[] = Array.isArray(response?.data?.data) ? [...response.data.data] : []
      // Billing's service picker (and the server library) must include every
      // accessible server, not silently stop after Panel's first 100 results.
      const totalPages = Number(response.data?.meta?.pagination?.total_pages || 1)
      if (Number.isSafeInteger(totalPages) && totalPages > 1) {
        for (let page = 2; page <= totalPages; page += 1) {
          const next = await this.client.get<{ data?: unknown[] }>('/panel/api/client', {
            params: { page, per_page: 100, include: 'nest,allocations' },
            headers: this.clientApiKey ? { Authorization: `Bearer ${this.clientApiKey}` } : undefined,
          })
          if (!Array.isArray(next.data?.data)) throw new Error('Server list page unavailable')
          list.push(...next.data.data)
        }
      }
      apiLogger.debug('Extracted list length:', list.length)
      
      if (list.length > 0) {
        apiLogger.debug('First server sample:', list[0])
        apiLogger.debug('First server attributes:', list[0]?.attributes)
        apiLogger.debug('First server relationships:', list[0]?.relationships)
        apiLogger.debug('First server allocations structure:', list[0]?.relationships?.allocations)
      }

      // The Pterodactyl API returns each server as { object: 'server', attributes: {...}, relationships: { allocations: { data: [...] } } }
      // For server list API (no include), allocations won't be present - that's expected
      // Individual server details API will include allocations automatically
      const flattened: Server[] = list.filter(item => !cancellationForServer(cancellations, item?.attributes?.uuid)).map((item: any) => {
        if (!item) return item
        const attrs = item.attributes || {}
        
        // Debug the incoming allocation structure (should be empty for server list)
        if (attrs.name === 'Vanilla Minecraft') {
          apiLogger.debug('Processing server:', attrs.name)
          apiLogger.trace('Full item structure:', item)
          apiLogger.trace('item.relationships:', item.relationships)
          apiLogger.trace('item.relationships.allocations:', item.relationships?.allocations)
          apiLogger.trace('item.relationships.allocations.data:', item.relationships?.allocations?.data)
        }
        
        // Extract allocations - Panel API puts relationships inside attributes
        let allocations: any[] = []
        try {
          // Check attributes.relationships first (Panel API structure)
          const rel = attrs.relationships?.allocations?.data
          if (Array.isArray(rel)) {
            allocations = rel.map((a: any) => a?.attributes || a).filter(Boolean)
            apiLogger.debug(`Server "${attrs.name}": Extracted ${allocations.length} allocations from attributes.relationships.allocations.data`)
            if (allocations.length > 0) {
              apiLogger.debug(`  First allocation:`, allocations[0])
            }
          } else {
            apiLogger.warn(`Server "${attrs.name}": No allocations found in attributes.relationships.allocations.data`)
          }
        } catch (err) {
          apiLogger.error(`Server "${attrs.name}": Error extracting allocations:`, err)
        }
        
        // Extract variables - Panel API puts relationships inside attributes
        let variables: any[] = []
        try {
          const relVars = attrs.relationships?.variables?.data
          if (Array.isArray(relVars)) variables = relVars.map((v: any) => v?.attributes || v).filter(Boolean)
        } catch {}
        
        // Determine if this is a database server based on multiple indicators
        // Since nest relationship is not available via Panel client API, use other reliable indicators
        let isDatabaseServer = false
        
        // Method 1: Check for direct nest_id (if available in attributes)
        if (attrs.nest_id === 5) {
          isDatabaseServer = true
        }
        
        // Method 2: Try nest relationship if included (fallback)
        const nestData = item.relationships?.nest?.data?.attributes || attrs.nest
        if (nestData && (nestData.name?.toLowerCase() === 'databases' || nestData.id === 5)) {
          isDatabaseServer = true
        }
        
        // Method 3: Check Docker image for database indicators (most reliable)
        if (!isDatabaseServer && attrs.docker_image) {
          const databaseImages = ['mariadb', 'mysql', 'postgres', 'postgresql', 'mongodb', 'redis', 'cassandra']
          const imageLower = attrs.docker_image.toLowerCase()
          isDatabaseServer = databaseImages.some(db => imageLower.includes(db))
        }
        
        // Method 4: Check server name for database indicators
        if (!isDatabaseServer && attrs.name) {
          const serverName = attrs.name.toLowerCase()
          const databaseKeywords = ['mariadb', 'mysql', 'database', 'postgres', 'postgresql', 'mongodb', 'redis', 'cassandra']
          isDatabaseServer = databaseKeywords.some(keyword => serverName.includes(keyword))
        }
        
        // Method 5: Check invocation command for database indicators
        if (!isDatabaseServer && attrs.invocation) {
          const invocation = attrs.invocation.toLowerCase()
          const databaseCommands = ['mariadb', 'mysql', 'postgres', 'mongod', 'redis', 'cassandra']
          isDatabaseServer = databaseCommands.some(cmd => invocation.includes(cmd))
        }
        
        // Detect specific database type for database servers
        let databaseType: 'mariadb' | 'postgresql' | 'mongodb' | null = null
        if (isDatabaseServer) {
          const detectDbType = (str: string): 'mariadb' | 'postgresql' | 'mongodb' | null => {
            if (!str) return null
            const lower = str.toLowerCase()
            if (lower.includes('postgres')) return 'postgresql'
            if (lower.includes('mongo')) return 'mongodb'
            if (lower.includes('mariadb') || lower.includes('mysql')) return 'mariadb'
            return null
          }
          
          // Check docker_image first (most reliable)
          if (attrs.docker_image) {
            databaseType = detectDbType(attrs.docker_image)
          }
          // Check invocation command
          if (!databaseType && attrs.invocation) {
            databaseType = detectDbType(attrs.invocation)
          }
          // Check server name as fallback
          if (!databaseType && attrs.name) {
            databaseType = detectDbType(attrs.name)
          }
        }
        
        return {
          ...attrs,
          // Provide defensive defaults if missing (prevents undefined property access in components)
          is_database_server: isDatabaseServer,
          database_type: databaseType,
          relationships: {
            allocations,
            variables,
          },
        } as Server
      })

      // Save cache and clear in-flight marker
      this.serversCache = { ts: Date.now(), data: flattened }
      return flattened
    })()

    this.serversCache = { ts: now, data: [], promise: p }
    try {
      return await p
    } finally {
      // promise cleared in the awaited function when it sets final cache
      // Ensure promise field is unset even if request failed
      if (this.serversCache) this.serversCache.promise = undefined
    }
  }

  // Fetch server created_at dates from Hoard (Panel API doesn't expose them)
  async getServerDates(): Promise<Record<string, string>> {
    const { data } = await this.client.get('/hoard-api/servers/dates', this.hoardJwtConfig())
    return data?.data || {}
  }

  // Fetch billing tier for each server (standard, premium_intel, premium_amd)
  async getServerTiers(): Promise<Record<string, string>> {
    const { data } = await this.client.get('/hoard-api/servers/tiers', this.hoardJwtConfig())
    return data?.data || {}
  }

  // Global search for servers (matches Panel's filter behavior)
  async searchServers(term: string, limit = 5): Promise<Array<{
    uuid: string
    identifier: string
    name: string
    node: string
    allocation?: { ip: string; ip_alias: string | null; port: number }
  }>> {
    if (!term || term.trim().length < 3) return []
    const [{ data }, cancellations] = await Promise.all([this.client.get('/panel/api/client', {
      params: { 'filter[*]': term, per_page: limit },
    }), getServerCancellations()])
    const list: any[] = Array.isArray(data?.data) ? data.data : []
    return list.filter(item => !cancellationForServer(cancellations, item?.attributes?.uuid)).map((d: any) => {
      const a = d?.attributes || {}
      // relationships.allocations.data[].attributes has { ip, ip_alias, port, is_default }
      const rel = d?.relationships?.allocations?.data
      let alloc: any = undefined
      if (Array.isArray(rel) && rel.length) {
        const def = rel.find((x: any) => x?.attributes?.is_default) || rel[0]
        if (def?.attributes) {
          alloc = {
            ip: def.attributes.ip,
            ip_alias: def.attributes.ip_alias ?? null,
            port: def.attributes.port,
          }
        }
      }
      return {
        uuid: a.uuid,
        identifier: a.identifier,
        name: a.name,
        node: a.node,
        allocation: alloc,
      }
    })
  }

  // Activity (Account)
  async getAccountActivity(
    { page = 1, perPage = 25, event, category, serverUuid, sort = '-timestamp', from, to }: { page?: number; perPage?: number; event?: string; category?: 'servers' | 'billing' | 'security' | 'access'; serverUuid?: string; sort?: 'timestamp' | '-timestamp' | 'newest' | 'oldest'; from?: string; to?: string }
  ): Promise<{ data: import('@/types/api').ActivityLog[]; pagination: ActivityPagination }> {
    // Use Hoard endpoint which filters out system API key events
    if (!this.clientApiKey) throw new Error('Not authenticated')
    
    const response = await this.client.get<ListEnvelope<Partial<import('@/types/api').ActivityLog>>>('/billing/account/activity', {
      params: {
        page,
        per_page: perPage,
        ...(event ? { event } : {}),
        ...(category ? { category } : {}),
        ...(serverUuid ? { server_uuid: serverUuid } : {}),
        sort,
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
      },
      headers: { Authorization: `Bearer ${this.clientApiKey}` },
    })
    
    const { data, meta } = response.data
    
    const mapped = (data || []).map(item => normalizeActivityLog(unwrapListEntry(item)))
    return { data: mapped, pagination: meta?.pagination || {} }
  }

  async getCustomerTasks(
    { page = 1, perPage = 10, status, category }: { page?: number; perPage?: number; status?: import('@/types/api').CustomerTaskStatus; category?: string } = {}
  ): Promise<{ data: import('@/types/api').CustomerTask[]; pagination: ActivityPagination; activeCount: number }> {
    if (!this.clientApiKey) throw new Error('Not authenticated')
    const response = await this.client.get<ListEnvelope<import('@/types/api').CustomerTask>>('/billing/account/activity', {
      params: {
        view: 'tasks',
        page,
        per_page: perPage,
        ...(status ? { status } : {}),
        ...(category ? { category } : {}),
      },
      headers: { Authorization: `Bearer ${this.clientApiKey}` },
    })
    const { data, meta } = response.data
    return {
      data: (data || []).map(item => unwrapListEntry(item)),
      pagination: meta?.pagination || {},
      activeCount: Number(meta?.active_count || 0),
    }
  }

  async getServer(uuid: string): Promise<Server> {
    const cancellation = cancellationForServer(await getServerCancellations(), uuid)
    if (cancellation) throw new ServerCancelledError(cancellation)
    // Support both full UUID and the short identifier (first segment / first 8 chars)
    const candidates: string[] = []
    if (uuid) {
      candidates.push(uuid)
      if (uuid.includes('-')) candidates.push(uuid.split('-')[0])
      if (!uuid.includes('-') && uuid.length > 8) candidates.push(uuid.substring(0, 8))
    }

    apiLogger.debug('Looking for server with candidates:', candidates)

    for (const id of [...new Set(candidates)]) {
      try {
        apiLogger.debug(`Trying /panel/api/client/servers/${id} with include=allocations,nest`)
        const res = await this.client.get<any>(`/panel/api/client/servers/${id}`, { params: { include: 'allocations,nest' } })
        const raw = res?.data
        if (!raw) continue

        apiLogger.trace('Raw individual server response:', raw)
        // Log the nest relationship and attributes for debugging
        if (raw.relationships) {
          apiLogger.trace('relationships:', raw.relationships);
        }
        if (raw.attributes) {
          apiLogger.trace('attributes:', raw.attributes);
        }
        // Raw shape: { object: 'server', attributes: { ... }, relationships: { allocations: { data: [ { attributes: {...} } ] } } }
        const attrs = raw.attributes || raw.data?.attributes || {}
        let allocations: any[] = []
        try {
          // The relationships are nested under attributes, not at root level
          const relAlloc = attrs.relationships?.allocations?.data || raw.relationships?.allocations?.data
          apiLogger.trace('Raw allocations data:', relAlloc)
          if (Array.isArray(relAlloc)) allocations = relAlloc.map((a: any) => a?.attributes || a).filter(Boolean)
        } catch (e) {
          apiLogger.error('Error extracting allocations:', e)
        }
        
        apiLogger.debug('Extracted allocations:', allocations)
        
        let variables: any[] = []
        try {
          // The relationships are nested under attributes, not at root level
          const relVars = attrs.relationships?.variables?.data || raw.relationships?.variables?.data
          if (Array.isArray(relVars)) variables = relVars.map((v: any) => v?.attributes || v).filter(Boolean)
        } catch {}
        
        // Determine if this is a database server based on multiple indicators
        // Since nest relationship is not available via Panel client API, use other reliable indicators
        let isDatabaseServer = false
        let databaseType: 'mariadb' | 'postgresql' | 'mongodb' | null = null
        
        // Helper to detect database type from a string
        const detectDbType = (str: string): 'mariadb' | 'postgresql' | 'mongodb' | null => {
          if (!str) return null
          const lower = str.toLowerCase()
          if (lower.includes('postgres') || lower.includes('postgresql') || lower.includes('pgsql')) {
            return 'postgresql'
          } else if (lower.includes('mongo') || lower.includes('mongodb')) {
            return 'mongodb'
          } else if (lower.includes('maria') || lower.includes('mysql')) {
            return 'mariadb'
          }
          return null
        }
        
        // Method 1: Check for direct nest_id (if available in attributes)
        if (attrs.nest_id === 5) {
          isDatabaseServer = true
          apiLogger.debug('Database server detected via nest_id:', attrs.nest_id)
        }
        
        // Method 2: Try nest relationship if included (fallback)
        const nestData = raw.relationships?.nest?.data?.attributes || attrs.nest
        apiLogger.trace('nestData:', nestData);
        if (nestData && (nestData.name?.toLowerCase() === 'databases' || nestData.id === 5)) {
          isDatabaseServer = true
          apiLogger.debug('Database server detected via nest relationship')
        }
        
        // Method 3: Check Docker image for database indicators (most reliable)
        if (!isDatabaseServer && attrs.docker_image) {
          const databaseImages = ['mariadb', 'mysql', 'postgres', 'postgresql', 'mongodb', 'redis', 'cassandra']
          const imageLower = attrs.docker_image.toLowerCase()
          isDatabaseServer = databaseImages.some(db => imageLower.includes(db))
          if (isDatabaseServer) {
            apiLogger.debug('Database server detected via docker image:', attrs.docker_image)
            databaseType = detectDbType(attrs.docker_image)
          }
        }
        
        // Method 4: Check server name for database indicators
        if (!isDatabaseServer && attrs.name) {
          const serverName = attrs.name.toLowerCase()
          const databaseKeywords = ['mariadb', 'mysql', 'database', 'postgres', 'postgresql', 'mongodb', 'redis', 'cassandra']
          isDatabaseServer = databaseKeywords.some(keyword => serverName.includes(keyword))
          if (isDatabaseServer) {
            apiLogger.debug('Database server detected via server name:', attrs.name)
            if (!databaseType) databaseType = detectDbType(attrs.name)
          }
        }
        
        // Method 5: Check invocation command for database indicators
        if (!isDatabaseServer && attrs.invocation) {
          const invocation = attrs.invocation.toLowerCase()
          const databaseCommands = ['mariadb', 'mysql', 'postgres', 'mongod', 'redis', 'cassandra']
          isDatabaseServer = databaseCommands.some(cmd => invocation.includes(cmd))
          if (isDatabaseServer) {
            apiLogger.debug('Database server detected via invocation:', attrs.invocation)
            if (!databaseType) databaseType = detectDbType(attrs.invocation)
          }
        }
        
        // Default to mariadb if database server but type unknown
        if (isDatabaseServer && !databaseType) {
          databaseType = 'mariadb'
        }
        
        apiLogger.debug('Final isDatabaseServer result:', isDatabaseServer, 'type:', databaseType)
        
        const result = {
          ...attrs,
          is_database_server: isDatabaseServer,
          database_type: databaseType,
          relationships: {
            allocations,
            variables,
          },
        } as Server
        
        // Fetch service_type from Hoard backend (non-blocking)
        // This provides authoritative service type classification based on egg/nest
        try {
          const fullUuid = attrs.uuid || id
          const serviceTypeData = await this.getServerServiceType(fullUuid)
          if (serviceTypeData?.service_type) {
            result.service_type = serviceTypeData.service_type
            apiLogger.debug('Service type from Hoard:', serviceTypeData.service_type)
            // Update is_database_server if Hoard says it's a database
            if (serviceTypeData.service_type === 'database') {
              result.is_database_server = true
            }
          }
        } catch (e) {
          apiLogger.debug('Could not fetch service_type from Hoard (falling back to local detection):', e)
        }
        
        apiLogger.trace('Final server object with allocations:', result)
        return result
      } catch (e: any) {
        apiLogger.error(`Failed to fetch server ${id}:`, e)
        // Non-404 errors should surface immediately
        if (e?.response?.status && e.response.status !== 404) throw e
        // else try next candidate
      }
    }
    throw new Error('Server not found')
  }

  /**
   * Fetch server service_type from Hoard backend.
   * This provides authoritative classification based on the server's egg/nest configuration.
   * Returns: { server_uuid: string, service_type: 'minecraft' | 'rust' | 'database' | 'standard' | ... }
   */
  async getServerServiceType(uuid: string): Promise<{ server_uuid: string; service_type: string } | null> {
    try {
      const response = await this.client.get<any>(
        `/hoard-api/servers/${uuid}/service-type`,
        this.hoardJwtConfig()
      )
      const data = response?.data?.data || response?.data
      if (data?.service_type) {
        return {
          server_uuid: data.server_uuid || uuid,
          service_type: data.service_type
        }
      }
      return null
    } catch (e: any) {
      // Log but don't throw - this is non-critical metadata
      apiLogger.debug(`Failed to fetch service_type for ${uuid}:`, e?.response?.status || e?.message)
      return null
    }
  }

  /**
   * Get available Minecraft eggs for server type switching.
   * Only available for Minecraft servers.
   */
  async getAvailableEggs(uuid: string): Promise<{
    current_egg_id: number
    current_egg_name: string
    eggs: Array<{
      id: number
      name: string
      profile_id?: string
      software?: string
      display_name?: string
      display_description?: string
      feature_bindings?: Record<string, string>
      docker_image: string
      docker_images: Record<string, string>
      is_current: boolean
    }>
  } | null> {
    try {
      const response = await this.client.get<any>(
        `/hoard-api/servers/${uuid}/eggs`,
        this.hoardJwtConfig()
      )
      return response?.data?.data || null
    } catch (e: any) {
      apiLogger.debug(`Failed to fetch eggs for ${uuid}:`, e?.response?.status || e?.message)
      return null
    }
  }

  /**
   * Switch a Minecraft server's egg (server type).
   * Changes the server from one type to another (e.g., Vanilla -> Forge).
   * 
   * @param uuid - Server UUID
   * @param options - Either egg_id or mcjars_type is required
   * @returns Success response with previous and new egg info
   */
  async switchServerEgg(
    uuid: string,
    options: {
      egg_id?: number
      mcjars_type?: string
      version?: string
      environment?: Record<string, string>
    }
  ): Promise<{
    success: boolean
    message: string
    previous_egg: { id: number; name: string }
    new_egg: { id: number; name: string | null }
  }> {
    const response = await this.client.patch<any>(
      `/hoard-api/servers/${uuid}/egg`,
      options,
      this.hoardJwtConfig()
    )
    return response.data
  }

  async getServerResources(uuid: string): Promise<any> {
    // Cache/dedupe per-server resource read to avoid back-to-back duplicate calls
    const TTL = 5_000 // 5s is fine for dashboard snapshots
    const now = Date.now()
    const key = String(uuid)
    const cached = this.resourcesCache.get(key)
    if (cached) {
      if (cached.promise) return cached.promise
      if (now - cached.ts < TTL) return cached.data
    }

    const prom = (async () => {
      // Let Axios reject 401 so the Panel-key recovery interceptor can run.
      // Other 4xx responses are not live resource samples either.
      const response = await this.client.get(`/panel/api/client/servers/${uuid}/resources`)
      if (response.status >= 400) throw new Error(`Panel resource request failed (${response.status})`)
    // Normalize various Panel payload shapes to a stable structure used by the UI
    const raw = (response as any)?.data
    // Common shapes:
    // - { object: 'stats', attributes: { current_state, resources: { cpu_absolute, memory_bytes, disk_bytes, ... } } }
    // - { data: { current_state, resources: { ... } } } (older/mocked)
    // - { current_state, resources } (already flattened)
    const attrs = raw?.attributes || raw?.data?.attributes || raw?.data || raw || {}
    const current_state = attrs.current_state ?? attrs.state ?? attrs.status ?? null
    const res = attrs.resources || attrs
    const normalized = {
      current_state,
      resources: {
        cpu_absolute: typeof res.cpu_absolute === 'number' ? res.cpu_absolute : (typeof res.cpu === 'number' ? res.cpu : 0),
        memory_bytes: typeof res.memory_bytes === 'number' ? res.memory_bytes : (typeof res.memory === 'number' ? res.memory : 0),
        disk_bytes: typeof res.disk_bytes === 'number' ? res.disk_bytes : (typeof res.disk === 'number' ? res.disk : 0),
        network: res.network,
        uptime: typeof res.uptime === 'number' ? res.uptime : undefined,
      }
    }
      this.resourcesCache.set(key, { ts: Date.now(), data: normalized })
      return normalized
    })()

    this.resourcesCache.set(key, { ts: now, data: null as any, promise: prom })
    try {
      return await prom
    } catch (error) {
      if (this.resourcesCache.get(key)?.promise === prom) this.resourcesCache.delete(key)
      throw error
    } finally {
      const entry = this.resourcesCache.get(key)
      if (entry) entry.promise = undefined
    }
  }

  async sendServerCommand(uuid: string, command: string): Promise<void> {
  await this.client.post(`/panel/api/client/servers/${uuid}/command`, { command })
  }

  /**
   * Helper to safely read and parse a JSON file from the server
   * Returns null if file doesn't exist or can't be parsed
   */
  private async safeReadJsonFile<T = any>(uuid: string, filePath: string): Promise<T | null> {
    try {
      const content = await this.getFileContents(uuid, filePath)
      if (!content || content.trim() === '') return null
      return JSON.parse(content) as T
    } catch (e: any) {
      // File doesn't exist or isn't valid JSON - that's okay
      apiLogger.debug(`Could not read ${filePath}:`, e?.message)
      return null
    }
  }

  /**
   * Get players for a Minecraft server by parsing server files:
   * - usercache.json: player names, UUIDs, last seen
   * - whitelist.json: whitelisted players
   * - banned-players.json: banned players with reasons
   * - banned-ips.json: banned IPs
   * - ops.json: operators with permission levels
   */
  async getServerPlayers(uuid: string): Promise<import('@/types/player').Player[]> {
    // Type definitions for Minecraft server files
    interface UserCacheEntry {
      name: string
      uuid: string
      expiresOn?: string
    }
    interface WhitelistEntry {
      uuid: string
      name: string
    }
    interface BannedPlayerEntry {
      uuid: string
      name: string
      created: string
      source: string
      expires: string | null
      reason: string
    }
    interface BannedIpEntry {
      ip: string
      created: string
      source: string
      expires: string | null
      reason: string
    }
    interface OpsEntry {
      uuid: string
      name: string
      level: number
      bypassesPlayerLimit?: boolean
    }

    try {
      // Fetch all player data files in parallel
      const [usercache, whitelist, bannedPlayers, , ops] = await Promise.all([
        this.safeReadJsonFile<UserCacheEntry[]>(uuid, '/usercache.json'),
        this.safeReadJsonFile<WhitelistEntry[]>(uuid, '/whitelist.json'),
        this.safeReadJsonFile<BannedPlayerEntry[]>(uuid, '/banned-players.json'),
        this.safeReadJsonFile<BannedIpEntry[]>(uuid, '/banned-ips.json'),
        this.safeReadJsonFile<OpsEntry[]>(uuid, '/ops.json'),
      ])

      // Build lookup maps for O(1) access
      const whitelistSet = new Set((whitelist || []).map(e => e.uuid.toLowerCase().replace(/-/g, '')))
      const bannedMap = new Map((bannedPlayers || []).map(e => [e.uuid.toLowerCase().replace(/-/g, ''), e]))
      const opsMap = new Map((ops || []).map(e => [e.uuid.toLowerCase().replace(/-/g, ''), e]))

      // If usercache is empty, we have no players
      if (!usercache || usercache.length === 0) {
        apiLogger.debug('No usercache.json found or empty, returning empty player list')
        return []
      }

      // Build player list from usercache
      const players: import('@/types/player').Player[] = usercache.map(entry => {
        const normalizedUuid = entry.uuid.toLowerCase().replace(/-/g, '')
        const ban = bannedMap.get(normalizedUuid)
        const op = opsMap.get(normalizedUuid)
        const isWhitelisted = whitelistSet.has(normalizedUuid)
        
        // Build groups array based on permissions
        const groups: string[] = []
        if (op) {
          if (op.level >= 4) groups.push('Owner')
          else if (op.level >= 3) groups.push('Admin')
          else if (op.level >= 2) groups.push('Moderator')
          else groups.push('Helper')
        }
        if (isWhitelisted) groups.push('Whitelisted')

        // Parse last seen from expiresOn (usercache entries expire 30 days after last login)
        // So last seen ≈ expiresOn - 30 days
        let lastSeen = new Date().toISOString()
        if (entry.expiresOn) {
          try {
            const expires = new Date(entry.expiresOn)
            const lastSeenDate = new Date(expires.getTime() - 30 * 24 * 60 * 60 * 1000)
            lastSeen = lastSeenDate.toISOString()
          } catch {
            // Use current time if parsing fails
          }
        }

        return {
          name: entry.name,
          uuid: entry.uuid,
          status: 'offline' as const, // Will be updated by WebSocket log parsing
          groups,
          lastSeen,
          banned: !!ban,
          banInfo: ban ? {
            reason: ban.reason || 'No reason specified',
            created: ban.created,
            source: ban.source,
            expires: ban.expires,
          } : undefined,
          whitelisted: isWhitelisted,
          isOp: !!op,
          opLevel: op?.level,
          knownIps: [], // IPs not stored in vanilla files per-player
          dataSource: 'vanilla' as const,
          // Plugin-only fields left undefined
          health: undefined,
          food: undefined,
          xpLevel: undefined,
          gamemode: undefined,
          position: undefined,
        }
      })

      apiLogger.debug(`Loaded ${players.length} players from server files`)
      return players
    } catch (e: any) {
      apiLogger.error('Failed to load players from server files:', e?.message)
      return []
    }
  }

  /**
   * Get banned IPs from banned-ips.json
   */
  async getServerBannedIps(uuid: string): Promise<import('@/types/player').BannedIP[]> {
    interface BannedIpEntry {
      ip: string
      created: string
      source: string
      expires: string | null
      reason: string
    }
    
    const bannedIps = await this.safeReadJsonFile<BannedIpEntry[]>(uuid, '/banned-ips.json')
    if (!bannedIps) return []
    
    return bannedIps.map(entry => ({
      ip: entry.ip,
      reason: entry.reason || undefined,
      createdAt: entry.created,
      expiresAt: entry.expires,
      createdBy: entry.source,
    }))
  }

  async setServerPowerState(uuid: string, signal: 'start' | 'stop' | 'restart' | 'kill'): Promise<void> {
    try {
      await this.client.post(`/panel/api/client/servers/${uuid}/power`, { signal })
    } catch (error) {
      throw new ServerPowerRequestError(signal, error)
    }
  }

  async getServerAllocations(uuid: string): Promise<Array<{ id: number; ip: string; port: number; notes?: string | null; is_default?: boolean }>> {
    try {
      const res = await this.client.get(`/panel/api/client/servers/${uuid}`, { params: { include: 'allocations' } })
      const raw = res?.data
      if (!raw) return []
      
      // Extract allocations from the response
      const attrs = raw.attributes || raw.data?.attributes || {}
      const relAlloc = attrs.relationships?.allocations?.data || raw.relationships?.allocations?.data
      
      if (!Array.isArray(relAlloc)) return []
      
      // Map to the expected format
      return relAlloc.map((a: any) => {
        const alloc = a?.attributes || a
        return {
          id: alloc.id,
          ip: alloc.ip,
          port: alloc.port,
          notes: alloc.notes ?? null,
          is_default: alloc.is_default ?? alloc.isDefault ?? false,
        }
      }).filter(Boolean)
    } catch (e) {
      apiLogger.error('Failed to fetch server allocations:', e)
      return []
    }
  }

  // Server files
  async getServerFiles(uuid: string, directory = '/'): Promise<any> {
    const response = await this.client.get(`/panel/api/client/servers/${uuid}/files/list`, {
      params: { directory },
      // Accept 404/500 silently — Wings returns 500 when a directory doesn't exist
      validateStatus: (status: number) => (status >= 200 && status < 300) || status === 404 || status === 500
    })
    // Return empty list for error status (missing directory)
    if (response.status === 404 || response.status === 500) {
      return { object: 'list', data: [] }
    }
    const raw = response.data
    const list = Array.isArray(raw?.data) ? raw.data : []
    // Normalize each entry to the shape expected by UI (Entry type in FilesPage)
    const norm = list.map((item: any) => {
      const a = item?.attributes || item
      return {
        name: a?.name || a?.entry || 'unknown',
        size: a?.size || 0,
        is_file: !!a?.is_file,
        is_symlink: !!a?.is_symlink,
        modified: a?.modified || a?.modified_at || '',
        mime: a?.mime || a?.mimetype || undefined,
        // permissions info (varies by panel version)
        mode: (typeof a?.mode !== 'undefined' ? a?.mode : (typeof a?.mode_numeric !== 'undefined' ? a?.mode_numeric : (typeof a?.permissions === 'number' ? a?.permissions : undefined))),
        mode_bits: a?.mode_bits || a?.modeBits || a?.permissions || a?.perms || undefined,
        permissions: a?.permissions_string || a?.perm_string || undefined,
      }
    })
    return { object: raw?.object || 'list', data: norm }
  }

  async getFileContents(uuid: string, file: string): Promise<string> {
    const response = await this.client.get(`/panel/api/client/servers/${uuid}/files/contents`, {
      params: { file },
      responseType: 'text', // Ensure we get raw text, not parsed JSON
      transformResponse: [(data) => data], // Prevent axios from parsing
    })
    apiLogger.debug('getFileContents response:', { file, dataType: typeof response.data, length: response.data?.length })
    return response.data || ''
  }

  async createFolder(uuid: string, directory: string, name: string): Promise<void> {
    await this.client.post(`/panel/api/client/servers/${uuid}/files/create-folder`, {
      root: directory || '/',
      name,
    })
  }

  async renameFile(uuid: string, directory: string, from: string, to: string): Promise<void> {
    await this.client.put(`/panel/api/client/servers/${uuid}/files/rename`, {
      root: directory || '/',
      files: [{ from, to }],
    })
  }

  async deleteFiles(uuid: string, directory: string, files: string[]): Promise<void> {
    await this.client.post(`/panel/api/client/servers/${uuid}/files/delete`, {
      root: directory || '/',
      files,
    })
  }

  async getDownloadUrl(uuid: string, file: string): Promise<string> {
    const res = await this.client.get(`/panel/api/client/servers/${uuid}/files/download`, { params: { file } })
    // Panel may return { data: { url } } or redirect; prefer JSON shape
    const url = (res as any)?.data?.data?.url || (res as any)?.data?.attributes?.url || (res as any)?.data?.url
    return url || ''
  }

  private async getUploadUrl(uuid: string): Promise<string> {
    const res = await this.client.get(`/panel/api/client/servers/${uuid}/files/upload`)
    const url = (res as any)?.data?.data?.url || (res as any)?.data?.attributes?.url || (res as any)?.data?.url
    return url || ''
  }

  async uploadFile(uuid: string, directory: string, file: File): Promise<void> {
    const url = await this.getUploadUrl(uuid)
    if (!url) throw new Error('Upload URL not available')
    const form = new FormData()
    form.append('files', file)
    form.append('directory', directory || '/')
    // Use a fresh axios without baseURL for the signed URL
    await axios.post(url, form, { headers: { 'Content-Type': 'multipart/form-data' } })
  }

  async moveFiles(uuid: string, files: string[], fromDir: string, toDir: string): Promise<void> {
    const pairs = files.map((name) => ({
      from: `${fromDir === '/' ? '' : fromDir}/${name}`.replace(/\/+/g, '/'),
      to: `${toDir === '/' ? '' : toDir}/${name}`.replace(/\\+/g, '/'),
    }))
    await this.client.put(`/panel/api/client/servers/${uuid}/files/rename`, {
      root: '/',
      files: pairs,
    })
  }

  /**
   * Pull a file from a remote URL directly to the server.
   * This allows downloading plugins, mods, etc. from external sources.
   */
  async pullFile(uuid: string, url: string, directory: string, filename?: string, foreground?: boolean): Promise<void> {
    await this.client.post(`/panel/api/client/servers/${uuid}/files/pull`, {
      url,
      directory,
      filename,
      use_header: !filename, // Use Content-Disposition header if no filename specified
      foreground: foreground ?? true, // Wait for completion by default
    })
  }

  /**
   * Decompress an archive (zip / tar.gz) on the server into the given root directory.
   * Wings extracts the archive contents in place.
   */
  async decompressFile(uuid: string, root: string, file: string): Promise<void> {
    await this.client.post(`/panel/api/client/servers/${uuid}/files/decompress`, {
      root,
      file,
    }, {
      // Extraction can take a while for large archives
      timeout: 120000,
    })
  }

  async copyFiles(uuid: string, files: string[], fromDir: string, toDir: string): Promise<void> {
    const pairs = files.map((name) => ({
      from: `${fromDir === '/' ? '' : fromDir}/${name}`.replace(/\\+/g, '/'),
      to: `${toDir === '/' ? '' : toDir}/${name}`.replace(/\\+/g, '/'),
    }))
    try {
      await this.client.post(`/panel/api/client/servers/${uuid}/files/copy`, {
        root: '/',
        files: pairs,
      })
    } catch {
      // Fallback: attempt content copy for files only
      for (const p of pairs) {
        try {
          const content = await this.getFileContents(uuid, p.from)
          await this.saveFileContents(uuid, p.to, content)
        } catch {
          // skip on error
        }
      }
    }
  }

  // Two-Factor Authentication (2FA)
  async getTwoFactorSetup(): Promise<{ image_url_data: string; secret: string }> {
    if (!this.clientApiKey) throw new Error('Not authenticated')

    const { data } = await this.client.get('/api/account/two-factor', {
      headers: { Authorization: `Bearer ${this.clientApiKey}` },
    })
    return (data?.data ?? data) as { image_url_data: string; secret: string }
  }

  async enableTwoFactor(code: string, password: string): Promise<string[]> {
    if (!this.clientApiKey) throw new Error('Not authenticated')

    const { data } = await this.client.post(
      '/api/account/two-factor',
      { code, password },
      { headers: { Authorization: `Bearer ${this.clientApiKey}` } }
    )
    return data?.attributes?.tokens || []
  }

  async disableTwoFactor(password: string): Promise<void> {
    if (!this.clientApiKey) throw new Error('Not authenticated')

    await this.client.post(
      '/api/account/two-factor/disable',
      { password },
      { headers: { Authorization: `Bearer ${this.clientApiKey}` } }
    )
  }

  async verifyTwoFactor(confirmationToken: string, code: string): Promise<AuthResponse> {
    const { data } = await this.client.post<AuthResponse>('/api/auth/login/checkpoint', {
      confirmation_token: confirmationToken,
      authentication_code: code
    })
    // If Hoard returns a client_api_key here, persist it like in login()
    if ((data as any)?.client_api_key) {
      this.setClientApiKey((data as any).client_api_key as string)
    }
    if ((data as any)?.access_token) {
      authSession.storeCredentials((data as any).access_token, (data as any).refresh_token)
    }
    return data
  }

  // Account API keys - Uses Hoard proxy to filter system keys
  async listApiKeys(): Promise<import('@/types/api').ApiKey[]> {
    if (!this.clientApiKey) throw new Error('Not authenticated')
    
    const { data } = await this.client.get('/billing/account/api-keys', {
      headers: { Authorization: `Bearer ${this.clientApiKey}` },
    })
    const list = Array.isArray(data?.data) ? data.data : []
    return list.map((d: any) => {
      const a = d.attributes || {}
      return {
        identifier: a.identifier,
        description: a.description,
        allowed_ips: a.allowed_ips || [],
        created_at: a.created_at || null,
        last_used_at: a.last_used_at || null,
      }
    })
  }

  async cleanupSystemApiKeys(): Promise<{ deleted: number }> {
    if (!this.clientApiKey) throw new Error('Not authenticated')
    
    const response = await this.client.post('/billing/account/api-keys/cleanup', {}, {
      headers: { Authorization: `Bearer ${this.clientApiKey}` },
    })
    
    return { deleted: response.data.deleted || 0 }
  }

  async createApiKey(description: string, allowedIps: string[]): Promise<{ key: import('@/types/api').ApiKey; secretToken: string }>
  {
    // Use Hoard endpoint which prevents system key creation
    if (!this.clientApiKey) throw new Error('Not authenticated')
    
    const { data } = await this.client.post('/billing/account/api-keys', {
      description,
      allowed_ips: allowedIps,
    }, {
      headers: { Authorization: `Bearer ${this.clientApiKey}` },
    })
    const a = data?.attributes || {}
    const key = {
      identifier: a.identifier,
      description: a.description,
      allowed_ips: a.allowed_ips || [],
      created_at: a.created_at || null,
      last_used_at: a.last_used_at || null,
    }
    const secretToken = data?.meta?.secret_token || ''
    return { key, secretToken }
  }

  async deleteApiKey(identifier: string): Promise<void> {
    // Use Hoard endpoint which prevents deletion of system keys
    if (!this.clientApiKey) throw new Error('Not authenticated')
    
    await this.client.delete(`/billing/account/api-keys/${identifier}`, {
      headers: { Authorization: `Bearer ${this.clientApiKey}` },
    })
  }

  // Account SSH keys
  async listSshKeys(): Promise<import('@/types/api').SshKey[]> {
    const { data } = await this.client.get('/panel/api/client/account/ssh-keys')
    const list = Array.isArray(data?.data) ? data.data : []
    return list.map((d: any) => {
      const a = d.attributes || {}
      return {
        name: a.name,
        fingerprint: a.fingerprint,
        public_key: a.public_key,
        created_at: a.created_at || null,
      }
    })
  }

  async createSshKey(name: string, publicKey: string): Promise<import('@/types/api').SshKey> {
    const { data } = await this.client.post('/panel/api/client/account/ssh-keys', {
      name,
      public_key: publicKey,
    })
    const a = data?.attributes || {}
    return {
      name: a.name,
      fingerprint: a.fingerprint,
      public_key: a.public_key,
      created_at: a.created_at || null,
    }
  }

  async deleteSshKey(fingerprint: string): Promise<void> {
    // Panel uses POST /ssh-keys/remove with fingerprint
    await this.client.post('/panel/api/client/account/ssh-keys/remove', { fingerprint })
  }

  async saveFileContents(uuid: string, file: string, contents: string): Promise<void> {
    // Different Panel versions accept POST or PUT for files/write.
    //  - Modern: POST /files/write?file=path with raw text body
    //  - Some builds: PUT /files/write?file=path
    const url = `/panel/api/client/servers/${uuid}/files/write`
    const base = {
      params: { file },
      headers: { 'Content-Type': 'text/plain' },
      validateStatus: (s: number) => s < 500,
    }
    try {
      const res = await this.client.post(url, contents, base)
      if (res.status >= 200 && res.status < 300) return
      // Fallthrough on unexpected 4xx
    } catch (e: any) {
      const code = e?.response?.status
      if (code && code !== 405 && code !== 404) throw e
    }
    // Retry with PUT for panels that require it
    await this.client.put(url, contents, {
      params: { file },
      headers: { 'Content-Type': 'text/plain' },
    })
  }

  async chmodFiles(uuid: string, root: string, files: Array<{ file: string; mode: string }>): Promise<void> {
    await this.client.post(`/panel/api/client/servers/${uuid}/files/chmod`, { root, files })
  }

  // Server databases
  async getServerDatabases(uuid: string): Promise<any[]> {
    const response = await this.client.get(`/panel/api/client/servers/${uuid}/databases`, { validateStatus: s => s < 500 })
    const raw = response?.data
    const list = Array.isArray(raw?.data) ? raw.data : []
    return list.map((item: any) => {
      const a = item?.attributes || item || {}
      // Host info can be in relationships.host.attributes or directly in a.host
      const hostRel = a.relationships?.host?.attributes || {}
      const hostDirect = a.host || {}
      const hostObj = typeof hostDirect === 'object' ? hostDirect : {}
      
      // Extract address: try relationships.host first, then direct host object, then host as string
      const hostAddress = hostRel.address || hostObj.address || hostObj.host || 
                          (typeof a.host === 'string' ? a.host : null) || 'localhost'
      // Extract port: try relationships.host first, then direct host object
      const hostPort = hostRel.port || hostObj.port || a.port || 3306
      
      return {
        id: a.id || item.id,
        name: a.name || a.database || a.connection_name || '',
        username: a.username || a.relationships?.password?.attributes?.username || '',
        password: a.relationships?.password?.attributes?.password || a.password || '',
        remote: a.connections_from || a.remote || '%',
        host: hostAddress,
        port: hostPort,
        max_connections: a.max_connections || 25,
      }
    })
  }

  async createDatabase(uuid: string, name: string, remote: string): Promise<any> {
    const response = await this.client.post(`/panel/api/client/servers/${uuid}/databases`, {
      database: name,
      remote
    })
    return response.data.data
  }

  async deleteDatabase(uuid: string, databaseId: string): Promise<void> {
    await this.client.delete(`/panel/api/client/servers/${uuid}/databases/${databaseId}`)
  }

  async resetDatabasePassword(uuid: string, databaseId: string): Promise<any> {
    const response = await this.client.post(`/panel/api/client/servers/${uuid}/databases/${databaseId}/rotate-password`)
    return response.data.data || response.data
  }

  // Enhanced Database Server Management APIs (proxied via /panel/api to Hoard)
  async getServerDatabaseUsers(uuid: string): Promise<any[]> {
    const response = await this.client.get(`/panel/api/servers/${uuid}/database-users`, this.hoardJwtConfig())
    return response.data?.data || response.data || []
  }

  async createDatabaseUser(uuid: string, userData: any): Promise<any> {
    const response = await this.client.post(`/hoard-api/servers/${uuid}/database-users`, userData, this.hoardJwtConfig())
    return response.data?.data || response.data
  }

  async deleteDatabaseUser(uuid: string, userId: string): Promise<void> {
    await this.client.delete(`/hoard-api/servers/${uuid}/database-users/${userId}`, this.hoardJwtConfig())
  }

  async resetDatabaseUserPassword(uuid: string, userId: string): Promise<string> {
    const response = await this.client.post(`/hoard-api/servers/${uuid}/database-users/${userId}/rotate-password`, {}, this.hoardJwtConfig())
    return response.data?.password || response.data?.data?.password || ''
  }

  async updateDatabaseUserPermissions(uuid: string, userId: string, permissions: string[]): Promise<any> {
    const response = await this.client.patch(`/hoard-api/servers/${uuid}/database-users/${userId}`, { permissions }, this.hoardJwtConfig())
    return response.data?.data || response.data
  }

  // Rust recurring wipe schedules (proxied via /hoard-api to Hoard)
  async getRustWipeSchedules(uuid: string): Promise<RustWipeSchedule[]> {
    const response = await this.client.get(`/hoard-api/servers/${uuid}/rust/wipe-schedules`, this.hoardJwtConfig())
    return response.data?.data || response.data || []
  }

  async createRustWipeSchedule(uuid: string, schedule: Partial<RustWipeSchedule>): Promise<RustWipeSchedule> {
    const response = await this.client.post(`/hoard-api/servers/${uuid}/rust/wipe-schedules`, schedule, this.hoardJwtConfig())
    return response.data?.data || response.data
  }

  async updateRustWipeSchedule(uuid: string, scheduleId: number, updates: Partial<RustWipeSchedule>): Promise<RustWipeSchedule> {
    const response = await this.client.patch(`/hoard-api/servers/${uuid}/rust/wipe-schedules/${scheduleId}`, updates, this.hoardJwtConfig())
    return response.data?.data || response.data
  }

  async deleteRustWipeSchedule(uuid: string, scheduleId: number): Promise<void> {
    await this.client.delete(`/hoard-api/servers/${uuid}/rust/wipe-schedules/${scheduleId}`, this.hoardJwtConfig())
  }

  // Game Server Database Association APIs (proxied via /panel/api to Hoard)
  async getGameServerDatabaseAssociations(uuid: string): Promise<any[]> {
    const response = await this.client.get(`/panel/api/servers/${uuid}/database-associations`, this.hoardJwtConfig())
    return response.data?.data || response.data || []
  }

  async associateDatabaseServer(uuid: string, associationData: {
    database_server_uuid: string
    package_intent_uuid?: string
  }): Promise<{
    id: string
    game_server_uuid: string
    database_server_uuid: string
    status: 'active'
    replayed: boolean
  }> {
    const response = await this.client.post(`/panel/api/servers/${uuid}/database-associations`, associationData, this.hoardJwtConfig())
    return response.data?.data || response.data
  }

  async removeDatabaseServerAssociation(uuid: string, associationId: string): Promise<void> {
    await this.client.delete(`/panel/api/servers/${uuid}/database-associations/${associationId}`, this.hoardJwtConfig())
  }

  async updateDatabaseServerAssociation(uuid: string, associationId: string, updates: any): Promise<any> {
    const response = await this.client.patch(`/panel/api/servers/${uuid}/database-associations/${associationId}`, updates, this.hoardJwtConfig())
    return response.data?.data || response.data
  }

  // Standard Server Database APIs
  async getServerAssociatedDatabases(uuid: string): Promise<any[]> {
    const response = await this.client.get(`/api/servers/${uuid}/databases`)
    return response.data?.data || response.data || []
  }

  async getDatabaseConnectionDetails(uuid: string, databaseId: string): Promise<any> {
    const response = await this.client.get(`/api/servers/${uuid}/databases/${databaseId}/connection`)
    return response.data?.data || response.data
  }

  async requestDatabaseForServer(uuid: string, requestData: any): Promise<any> {
    const response = await this.client.post(`/api/servers/${uuid}/databases/request`, requestData)
    return response.data?.data || response.data
  }

  // Enhanced Database Management APIs
  async createDatabaseOnServer(uuid: string, databaseData: any): Promise<any> {
    const response = await this.client.post(`/api/servers/${uuid}/databases`, databaseData)
    return response.data?.data || response.data
  }

  async getServerDatabaseDetails(uuid: string, databaseId: string): Promise<any> {
    const response = await this.client.get(`/api/servers/${uuid}/databases/${databaseId}`)
    return response.data?.data || response.data
  }

  async deleteDatabaseFromServer(uuid: string, databaseId: string): Promise<void> {
    await this.client.delete(`/api/servers/${uuid}/databases/${databaseId}`)
  }  // Utility methods
  isAuthenticated(): Promise<boolean> {
    return this.client.get('/panel/api/client/account')
      .then(() => true)
      .catch(() => false)
  }

  /**
   * Transform a raw Wings WebSocket URL to route through the same-origin nginx proxy.
   * This ensures the Origin header matches Wings' PanelLocation config, avoiding
   * silent WebSocket upgrade rejections.
   *
   * Example: wss://node1.example.com:8080/api/servers/uuid/ws
   * → wss://{page_host}/panel/wings-ws/api/servers/uuid/ws?host=node1.example.com&scheme=https&port=8080
   */
  private toWsProxyUrl(wingsSocketUrl: string): string {
    // Standalone Vite development can connect directly to Wings. The local
    // platform runs Vite behind the nginx ownership boundary, so opt it into
    // the same-origin proxy used by production.
    const env = typeof import.meta !== 'undefined' ? (import.meta as any).env : undefined
    if (env?.DEV && env?.VITE_WINGS_PROXY_ENABLED !== 'true') {
      return wingsSocketUrl
    }
    try {
      const url = new URL(wingsSocketUrl)
      const scheme = url.protocol === 'wss:' ? 'https' : 'http'
      const host = url.hostname
      const port = url.port // empty string if default
      const path = url.pathname // /api/servers/{uuid}/ws

      const wsProto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const params = new URLSearchParams()
      params.set('host', host)
      params.set('scheme', scheme)
      if (port) params.set('port', port)

      return `${wsProto}//${window.location.host}/panel/wings-ws${path}?${params.toString()}`
    } catch {
      // If URL parsing fails, fall back to direct connection
      return wingsSocketUrl
    }
  }

  async getWebSocketUrl(uuid: string): Promise<{ socket: string; token: string }> {
    const tryFetch = async (id: string) => {
      const res = await this.client.get(`/panel/api/client/servers/${id}/websocket`, { validateStatus: s => s < 500 })
      if (res.status === 404) throw new Error('not-found')
      if (res.status === 429) throw new Error('rate-limited')
      const d = (res as any)?.data?.data || (res as any)?.data
      if (!d) throw new Error('Invalid response format')
      
      // Handle different response formats
      let token = d.token
      let socket = d.socket
      
      // Legacy support where token might be in 'origin' field
      if (d.origin && !token) {
        token = d.origin
      }
      
      if (!token || !socket) {
        throw new Error('missing-token-or-socket')
      }
      // Route through same-origin nginx proxy so the Origin header matches Wings' config.
      // In dev mode, falls back to direct connection.
      return { socket: this.toWsProxyUrl(socket), token }
    }
    const shortId = uuid.includes('-') ? uuid.split('-')[0] : (uuid.length > 8 ? uuid.substring(0, 8) : uuid)

    // Strategy: try full UUID first; on 404 or missing fields, immediately try short id; if still failing due
    // to early install race (missing-token-or-socket), wait ~350ms and retry once more (full then short).
    const quickRetryDelay = 350

    // Attempt 1: full UUID
    try {
      return await tryFetch(uuid)
    } catch (e1: any) {
      const msg1 = String(e1?.message || '')
      // Don't retry on rate-limit — propagate immediately so callers can back off
      if (msg1.includes('rate-limited')) throw e1
      // Attempt 1b: short identifier fallback for not-found OR partial payloads
      if (msg1.includes('not-found') || msg1.includes('missing-token-or-socket')) {
        try {
          return await tryFetch(shortId)
        } catch (e1b: any) {
          const msg1b = String(e1b?.message || '')
          if (msg1b.includes('rate-limited')) throw e1b
          if (msg1b.includes('missing-token-or-socket')) {
            // Quick retry once after a short delay
            await new Promise(r => setTimeout(r, quickRetryDelay))
            try {
              return await tryFetch(uuid)
            } catch (e2: any) {
              const msg2 = String(e2?.message || '')
              if (msg2.includes('rate-limited')) throw e2
              if (msg2.includes('missing-token-or-socket') || msg2.includes('not-found')) {
                return await tryFetch(shortId)
              }
              throw e2
            }
          }
          throw e1b
        }
      }
      throw e1
    }
  }

  async getServerConsoleLogs(uuid: string, tail = 500): Promise<{ lines: string[]; total: number }> {
    const res = await this.client.get(`/hoard-api/servers/${uuid}/console-logs`, {
      params: { tail },
      ...this.hoardJwtConfig(),
    })
    return res.data
  }

  // Schedules
  async listSchedules(uuid: string): Promise<any[]> {
    const { data } = await this.client.get(`/panel/api/client/servers/${uuid}/schedules`)
    if (!Array.isArray(data?.data)) return []
    return data.data.map((entry: any) => {
      const attributes = entry?.attributes || entry
      const taskEntries = entry?.relationships?.tasks?.data
        || attributes?.relationships?.tasks?.data
        || attributes?.tasks
        || []
      const tasks = Array.isArray(taskEntries)
        ? taskEntries.map((task: any) => task?.attributes || task)
        : []
      const cron = attributes?.cron || {}
      return {
        ...attributes,
        minute: attributes?.minute ?? cron.minute,
        hour: attributes?.hour ?? cron.hour,
        day_of_month: attributes?.day_of_month ?? cron.day_of_month,
        month: attributes?.month ?? cron.month,
        day_of_week: attributes?.day_of_week ?? cron.day_of_week,
        tasks,
      }
    })
  }
  async getSchedule(uuid: string, scheduleId: number | string): Promise<any> {
    const { data } = await this.client.get(`/panel/api/client/servers/${uuid}/schedules/${scheduleId}`, {
      validateStatus: (s) => s < 500,
    })
    // Prefer attributes shape; fall back to raw
    return (data?.attributes || data?.data?.attributes || data)
  }
  async listScheduleTasks(uuid: string, scheduleId: number | string): Promise<any[]> {
    try {
      const { data } = await this.client.get(`/panel/api/client/servers/${uuid}/schedules/${scheduleId}/tasks`, {
        validateStatus: (s) => s < 500,
      })
      if (Array.isArray(data?.data)) return data.data.map((d: any) => d.attributes || d)
      if (Array.isArray(data)) return data
      return []
    } catch {
      return []
    }
  }
  async createSchedule(uuid: string, payload: any): Promise<any> {
    // 1) Create the schedule with cron fields at the top level (Panel Client API contract)
    const body = {
      name: payload?.name,
      is_active: payload?.is_active ?? true,
      only_when_online: payload?.only_when_online ?? payload?.onlyWhenOnline ?? false,
      minute: payload?.cron?.minute ?? payload?.minute ?? '*',
      hour: payload?.cron?.hour ?? payload?.hour ?? '*',
      day_of_month: payload?.cron?.day_of_month ?? payload?.cron?.dayOfMonth ?? payload?.day_of_month ?? payload?.dayOfMonth ?? '*',
      month: payload?.cron?.month ?? payload?.month ?? '*',
      day_of_week: payload?.cron?.day_of_week ?? payload?.cron?.dayOfWeek ?? payload?.day_of_week ?? payload?.dayOfWeek ?? '*',
    }

    apiLogger.debug('Creating schedule (flattened cron):', body)
    const createRes = await this.client.post(`/panel/api/client/servers/${uuid}/schedules`, body)
    const created = (createRes as any)?.data?.attributes || (createRes as any)?.data?.data?.attributes || (createRes as any)?.data

    // Attempt to extract an id for subsequent task creation
    const scheduleId = created?.id ?? (createRes as any)?.data?.id ?? (createRes as any)?.data?.data?.id

    // 2) Optionally create tasks on the dedicated endpoint
    const tasks: any[] = Array.isArray(payload?.tasks) ? payload.tasks : []
    if (tasks.length && !scheduleId) {
      throw new Error('Panel created the schedule without returning an identifier')
    }
    if (scheduleId && tasks.length) {
      try {
        for (const t of tasks) {
          await this.createScheduleTask(uuid, scheduleId, {
            action: t.action,
            // Panel expects a string payload; stringify objects defensively
            payload: typeof t.payload === 'string' ? t.payload : (t.payload != null ? JSON.stringify(t.payload) : ''),
            time_offset: t.timeOffset ?? t.time_offset ?? 0,
            continue_on_failure: t.continueOnFailure ?? t.continue_on_failure ?? true,
            sequence_id: t.sequenceId ?? t.sequence_id,
            is_queued: t.isQueued ?? t.is_queued ?? false,
          })
        }
      } catch (error) {
        apiLogger.error('Failed to create schedule tasks; removing incomplete schedule', error)
        await this.deleteSchedule(uuid, scheduleId).catch(() => undefined)
        throw error
      }
    }

    return created
  }
  async createScheduleTask(uuid: string, scheduleId: number | string, task: { action: string; payload?: any; time_offset?: number; continue_on_failure?: boolean; sequence_id?: number; is_queued?: boolean }): Promise<any> {
    const taskBody = {
      action: task.action,
      payload: typeof task.payload === 'string' ? task.payload : (task.payload != null ? JSON.stringify(task.payload) : ''),
      time_offset: task.time_offset ?? 0,
      continue_on_failure: typeof task.continue_on_failure === 'boolean' ? task.continue_on_failure : true,
      ...(typeof task.sequence_id === 'number' ? { sequence_id: task.sequence_id } : {}),
      ...(typeof task.is_queued === 'boolean' ? { is_queued: task.is_queued } : {}),
    }
    const { data } = await this.client.post(`/panel/api/client/servers/${uuid}/schedules/${scheduleId}/tasks`, taskBody)
    return data?.attributes || data
  }
  async updateScheduleTask(uuid: string, scheduleId: number | string, taskId: number | string, task: { action?: string; payload?: any; time_offset?: number; continue_on_failure?: boolean; sequence_id?: number; is_queued?: boolean }): Promise<any> {
    const body: any = {}
    if (task.action != null) body.action = task.action
    if (task.payload !== undefined) body.payload = typeof task.payload === 'string' ? task.payload : (task.payload != null ? JSON.stringify(task.payload) : '')
    if (task.time_offset != null) body.time_offset = task.time_offset
    if (typeof task.continue_on_failure === 'boolean') body.continue_on_failure = task.continue_on_failure
    if (typeof task.sequence_id === 'number') body.sequence_id = task.sequence_id
    if (typeof task.is_queued === 'boolean') body.is_queued = task.is_queued
    const { data } = await this.client.post(`/panel/api/client/servers/${uuid}/schedules/${scheduleId}/tasks/${taskId}`, body)
    return data?.attributes || data
  }
  async deleteScheduleTask(uuid: string, scheduleId: number | string, taskId: number | string): Promise<void> {
    await this.client.delete(`/panel/api/client/servers/${uuid}/schedules/${scheduleId}/tasks/${taskId}`)
  }
  async deleteSchedule(uuid: string, scheduleId: number | string): Promise<void> {
    await this.client.delete(`/panel/api/client/servers/${uuid}/schedules/${scheduleId}`)
  }
  async updateSchedule(uuid: string, scheduleId: number | string, payload: { name?: string; is_active?: boolean; only_when_online?: boolean; minute?: string; hour?: string; day_of_month?: string; month?: string; day_of_week?: string }): Promise<any> {
    const body = {
      ...(payload.name != null ? { name: payload.name } : {}),
      ...(typeof payload.is_active === 'boolean' ? { is_active: payload.is_active } : {}),
      ...(typeof payload.only_when_online === 'boolean' ? { only_when_online: payload.only_when_online } : {}),
      minute: payload.minute ?? '*',
      hour: payload.hour ?? '*',
      day_of_month: payload.day_of_month ?? '*',
      month: payload.month ?? '*',
      day_of_week: payload.day_of_week ?? '*',
    }
    const { data } = await this.client.post(`/panel/api/client/servers/${uuid}/schedules/${scheduleId}`, body)
    return (data as any)?.attributes || data
  }
  // Compatibility helpers for pages expecting these methods
  async toggleSchedule(uuid: string, scheduleId: number | string, enable?: boolean): Promise<void> {
    // Some Panels do not support PATCH for schedules; use POST with full body.
    let attrs: any = null
    try {
      const res = await this.client.get(`/panel/api/client/servers/${uuid}/schedules/${scheduleId}`, { validateStatus: (s: number) => s < 500 })
      attrs = (res as any)?.data?.attributes || (res as any)?.data?.data?.attributes || (res as any)?.data || null
    } catch {}

    if (!attrs) {
      // Fallback: locate in list
      try {
        const list = await this.listSchedules(uuid)
        attrs = Array.isArray(list) ? list.find((s: any) => String(s.id ?? s.uuid) === String(scheduleId)) : null
      } catch {}
    }

    const currentActive: boolean = typeof attrs?.is_active === 'boolean' ? attrs.is_active : true
    const desiredActive = typeof enable === 'boolean' ? enable : !currentActive

    const body = {
      name: attrs?.name || 'Schedule',
      is_active: desiredActive,
      only_when_online: attrs?.only_when_online ?? false,
      minute: attrs?.minute ?? attrs?.cron?.minute ?? '*',
      hour: attrs?.hour ?? attrs?.cron?.hour ?? '*',
      day_of_month: attrs?.day_of_month ?? attrs?.cron?.dayOfMonth ?? attrs?.cron?.day_of_month ?? '*',
      month: attrs?.month ?? attrs?.cron?.month ?? '*',
      day_of_week: attrs?.day_of_week ?? attrs?.cron?.dayOfWeek ?? attrs?.cron?.day_of_week ?? '*',
    }

    await this.client.post(`/panel/api/client/servers/${uuid}/schedules/${scheduleId}`, body)
  }
  async triggerSchedule(uuid: string, scheduleId: number | string): Promise<void> {
    // Try common variants: /execute then /trigger
    const base = `/panel/api/client/servers/${uuid}/schedules/${scheduleId}`
    const opts = { validateStatus: (s: number) => s < 500 }
    try {
      const response = await this.client.post(`${base}/execute`, {}, opts)
      if (response.status >= 200 && response.status < 300) return
      if (response.status !== 404 && response.status !== 405) {
        throw new Error(`Schedule execution failed with status ${response.status}`)
      }
    } catch (e: any) {
      const code = e?.response?.status
      if (code && code !== 404 && code !== 405) throw e
    }
    const fallback = await this.client.post(`${base}/trigger`, {}, opts)
    if (fallback.status < 200 || fallback.status >= 300) {
      throw new Error(`Schedule trigger failed with status ${fallback.status}`)
    }
  }

  // Subusers (Users)
  async listSubusers(uuid: string): Promise<any[]> {
    const { data } = await this.client.get(`/panel/api/client/servers/${uuid}/users`)
    return Array.isArray(data?.data) ? data.data.map((d: any) => d.attributes || d) : []
  }
  async createSubuser(uuid: string, emailOrOpts: string | { email: string; permissions?: string[] }, permissions?: string[]): Promise<any> {
    // Normalize arguments (support both old and new call styles)
    const email = typeof emailOrOpts === 'string' ? emailOrOpts : emailOrOpts.email
    const perms = (typeof emailOrOpts === 'object' && emailOrOpts.permissions) ? emailOrOpts.permissions : (permissions || [])
    // Create subuser in Panel
    const { data } = await this.client.post(`/panel/api/client/servers/${uuid}/users`, { email, permissions: perms })
    const subuser = data?.attributes || data
    
    // Send email notification via Hoard (non-blocking)
    try {
      const [server, account] = await Promise.all([
        this.getServer(uuid),
        this.getAccount()
      ])
      
      // Extract username from email (before @)
      const username = email.split('@')[0]
      
      await this.sendSubuserAddedEmail(
        email,
        username,
        server.name,
        uuid,
        account.username || account.email?.split('@')[0] || 'Server Owner',
        perms
      )
    } catch (emailError) {
      apiLogger.error('Failed to send subuser added email, but subuser was created:', emailError)
    }
    
    return subuser
  }

  async updateSubuser(uuid: string, subuserUuid: string, permissions: string[]): Promise<any> {
    const { data } = await this.client.post(`/panel/api/client/servers/${uuid}/users/${subuserUuid}`, { permissions })
    return data?.attributes || data
  }

  async deleteSubuser(uuid: string, userIdOrUuid: string | number): Promise<void> {
    // Get subuser details before deletion (for email)
    let subuserEmail: string | null = null
    let subuserUsername: string | null = null
    let serverName: string | null = null
    let ownerUsername: string | null = null
    
    try {
      const [subusers, server, account] = await Promise.all([
        this.listSubusers(uuid),
        this.getServer(uuid),
        this.getAccount()
      ])
      
      const subuser = subusers.find((s: any) => 
        s.uuid === userIdOrUuid || s.id === userIdOrUuid
      )
      
      if (subuser) {
        subuserEmail = subuser.email
        subuserUsername = subuser.username || subuser.email?.split('@')[0]
        serverName = server.name
        ownerUsername = account.username || account.email?.split('@')[0] || 'Server Owner'
      }
    } catch (fetchError) {
      apiLogger.error('Failed to fetch subuser details for email:', fetchError)
    }
    
    // Delete subuser from Panel
    await this.client.delete(`/panel/api/client/servers/${uuid}/users/${userIdOrUuid}`)
    
    // Send email notification if we have the details (non-blocking)
    if (subuserEmail && subuserUsername && serverName && ownerUsername) {
      this.sendSubuserRemovedEmail(
        subuserEmail,
        subuserUsername,
        serverName,
        ownerUsername
      ).catch(emailError => {
        apiLogger.error('Failed to send subuser removed email:', emailError)
      })
    }
  }

  // Backups
  async listBackups(uuid: string): Promise<any[]> {
    const { data } = await this.client.get(`/panel/api/client/servers/${uuid}/backups`)
    return Array.isArray(data?.data) ? data.data.map((d: any) => d.attributes || d) : []
  }
  async createBackup(uuid: string, name: string): Promise<any> {
    const { data } = await this.client.post(
      `/panel/api/client/servers/${uuid}/backup-management/backups`,
      { name, source: 'manual' },
    )
    return data?.data || data
  }
  async createManagedBackup(
    uuid: string,
    input: { name: string; source?: BackupSource; is_locked?: boolean },
  ): Promise<BackupOperation> {
    try {
      const { data } = await this.client.post(
        `/panel/api/client/servers/${uuid}/backup-management/backups`,
        input,
      )
      return data?.data || data
    } catch (failure) {
      const detail = axios.isAxiosError(failure) ? failure.response?.data?.error : null
      throw new Error(typeof detail === 'string' && detail.trim()
        ? detail : 'The backup could not be started. Your server settings were not changed.')
    }
  }
  async createManagedBackupAndWait(
    uuid: string,
    input: { name: string; source?: BackupSource; is_locked?: boolean },
    options: { timeoutMs?: number; pollIntervalMs?: number } = {},
  ): Promise<BackupOperation> {
    const operation = await this.createManagedBackup(uuid, input)
    const timeoutMs = Math.max(options.timeoutMs ?? 15 * 60 * 1000, 1)
    const pollIntervalMs = Math.max(options.pollIntervalMs ?? 2000, 100)
    const deadline = Date.now() + timeoutMs

    while (Date.now() < deadline) {
      const management = await this.getBackupManagement(uuid)
      const current = management.operations.find(
        candidate => candidate.operation_id === operation.operation_id,
      )
      if (current?.status === 'completed') return current
      if (current?.status === 'failed') {
        throw new Error(
          current.error_message || current.message || 'The safety backup failed',
        )
      }
      await new Promise(resolve => setTimeout(resolve, pollIntervalMs))
    }

    throw new Error(
      'The safety backup did not finish in time; no server settings were changed',
    )
  }
  async getBackup(uuid: string, backupId: string): Promise<any> {
    const { data } = await this.client.get(`/panel/api/client/servers/${uuid}/backups/${backupId}`)
    return data?.attributes || data
  }
  async deleteBackup(uuid: string, backupId: string, confirmLocked = false): Promise<void> {
    await this.client.delete(
      `/panel/api/client/servers/${uuid}/backup-management/backups/${backupId}`,
      { data: { confirm_locked: confirmLocked } },
    )
  }
  async getBackupDownloadUrl(_uuid: string, backupId: string): Promise<string> {
    const { data } = await billingApi.post<{ data: { url: string } }>(`/hoard-api/account/backups/${backupId}/download`)
    return data.data.url
  }
  async toggleBackupLock(_uuid: string, backupId: string, isLocked: boolean): Promise<void> {
    await billingApi.put(`/hoard-api/account/backups/${backupId}/protection`, { is_locked: !isLocked })
  }
  async restoreBackup(
    uuid: string,
    backupId: string,
    targetServerUuid?: string,
  ): Promise<BackupOperation> {
    const { data } = await this.client.post(
      `/panel/api/client/servers/${uuid}/backup-management/backups/${backupId}/restore`,
      targetServerUuid ? { target_server_uuid: targetServerUuid } : {},
    )
    return data?.data || data
  }
  async verifyBackup(_uuid: string, _backupId: string): Promise<void> {
    // Pterodactyl doesn't have a verify endpoint - verification happens on download/restore
    // This is a placeholder for client-side verification logic
    throw new Error('Backup verification is handled automatically by Pterodactyl')
  }
  async cleanupBackups(uuid: string): Promise<void> {
    await this.client.post(`/panel/api/client/servers/${uuid}/backups/cleanup`)
  }
  async getBackupManagement(uuid: string): Promise<BackupManagement> {
    const { data } = await this.client.get(
      `/panel/api/client/servers/${uuid}/backup-management`,
    )
    return data?.data || data
  }
  async updateBackupSchedule(
    uuid: string,
    schedule: {
      enabled: boolean
      frequency: 'daily' | 'weekly'
      time: string
      timezone: string
      days_of_week: number[]
      retention_mode: 'keep_all' | 'days' | 'count'
      retention_value?: number | null
    },
  ): Promise<BackupSchedule> {
    const { data } = await this.client.put(
      `/panel/api/client/servers/${uuid}/backup-management/schedule`,
      schedule,
    )
    return data?.data || data
  }
  async disableBackupSchedule(uuid: string): Promise<void> {
    await this.client.delete(
      `/panel/api/client/servers/${uuid}/backup-management/schedule`,
    )
  }

  // Startup
  async getStartup(uuid: string): Promise<{ invocation: string; variables: any[]; dockerImages: Record<string, string> }> {
    const { data } = await this.client.get(`/panel/api/client/servers/${uuid}/startup`)
    
    apiLogger.debug('Raw API response:', data)
    
    // Pterodactyl returns a fractal collection with meta data
    // Structure: { data: [...variables], meta: { startup_command: "...", raw_startup_command: "...", docker_images: {...} } }
    let variables = Array.isArray(data?.data) ? data.data : []
    const startupCommand = data?.meta?.startup_command || data?.meta?.raw_startup_command || ''
    const dockerImages = data?.meta?.docker_images || {}
    
    apiLogger.debug('Initial variables extraction:', { variables, startupCommand, dockerImages })
    
    // Transform variables to extract attributes (Pterodactyl fractal format)
    variables = variables.map((v: any) => {
      if (v.attributes) {
        return v.attributes
      }
      return v
    })
    
    // Also check if variables are in relationships (fallback)
    if (variables.length === 0) {
      apiLogger.debug('No variables in data.data, checking relationships...')
      try {
        const relVars = data?.relationships?.variables?.data
        if (Array.isArray(relVars)) {
          variables = relVars.map((v: any) => v?.attributes || v).filter(Boolean)
          apiLogger.debug('Found variables in relationships:', variables)
        }
      } catch (e) {
        apiLogger.debug('Error checking relationships:', e)
      }
      
      // Final fallback to direct attributes
      if (variables.length === 0 && Array.isArray(data?.attributes?.variables)) {
        variables = data.attributes.variables
        apiLogger.debug('Found variables in attributes:', variables)
      }
    }
    
    apiLogger.debug('Final variables to return:', variables)
    
    return { 
      invocation: startupCommand || '', 
      variables,
      dockerImages
    }
  }
  async updateStartupVariable(uuid: string, variableId: number | string, value: string): Promise<void> {
    const body: any = typeof variableId === 'number' ? { id: variableId, value } : { key: variableId, value }
    const path = `/panel/api/client/servers/${uuid}/startup/variable`
    try {
      const res = await this.client.put(path, body, { validateStatus: (s: number) => s < 500 })
      if (res.status >= 200 && res.status < 300) return
    } catch (e: any) {
      const code = e?.response?.status
      if (code && code !== 405 && code !== 404 && code !== 422 && code !== 500) throw e
    }
    // Try POST fallback (some Panels accept POST for this route)
    await this.client.post(path, body)
  }
  async setDockerImage(uuid: string, image: string): Promise<void> {
    await this.client.put(`/panel/api/client/servers/${uuid}/settings/docker-image`, { docker_image: image })
  }

  // Network
  async setPrimaryAllocation(uuid: string, allocationId: number): Promise<void> {
    await this.client.post(`/panel/api/client/servers/${uuid}/network/allocations/${allocationId}/primary`, {})
  }

  // Settings
  async renameServer(uuid: string, name: string, description?: string): Promise<void> {
    // First, rename the server via Panel API
    await this.client.post(`/panel/api/client/servers/${uuid}/settings/rename`, { name, description })
    
    // Then, sync SERVER_NAME environment variable via Hoard
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {}
      await this.client.post(`/hoard-api/servers/${uuid}/rename`, { name }, config)
    } catch (e) {
      // Log but don't fail the rename operation if Hoard sync fails
      apiLogger.warn('Failed to sync SERVER_NAME variable:', e)
    }
  }
  async reinstallServer(uuid: string): Promise<void> {
    await this.client.post(`/panel/api/client/servers/${uuid}/settings/reinstall`, {})
  }

  async getProjectZomboidVersionSwitch(uuid: string): Promise<ProjectZomboidSwitchOperation> {
    const response = await this.hoardApiRequest(
      'get',
      `/hoard-api/servers/${uuid}/project-zomboid/version-switch`,
    )
    return response.data?.data || response.data
  }

  async startProjectZomboidVersionSwitch(
    uuid: string,
    selection: ProjectZomboidVersionSelection,
    createBackup: boolean,
  ): Promise<ProjectZomboidSwitchOperation> {
    const response = await this.hoardApiRequest(
      'post',
      `/hoard-api/servers/${uuid}/project-zomboid/version-switch`,
      {
        project_zomboid_version: selection,
        create_backup: createBackup,
      },
      { timeoutMs: 20000 },
    )
    return response.data?.data || response.data
  }

  // Server Activity (may not be available in all versions)
  async getServerActivity(uuid: string, { page = 1, perPage = 25, event, sort = '-timestamp' }: { page?: number; perPage?: number; event?: string; sort?: string } = {}): Promise<{ data: import('@/types/api').ActivityLog[]; pagination: ActivityPagination }> {
    const params: Record<string, string | number> = {
      page, 
      per_page: perPage,
      sort
    }
    if (event) {
      params['filter[event]'] = event
    }
    const res = await this.client.get<ListEnvelope<Partial<import('@/types/api').ActivityLog>>>(`/panel/api/client/servers/${uuid}/activity`, { params })
    const { data, meta } = res.data
    return {
      data: Array.isArray(data)
        ? data.map(item => normalizeActivityLog(unwrapListEntry(item)))
        : [],
      pagination: meta?.pagination || {},
    }
  }

  // Batch resource loading for better dashboard performance
  async getBatchServerResources(serverIds: string[]): Promise<Record<string, any>> {
    if (!serverIds.length) return {}
    
    // Use Promise.allSettled to handle individual failures gracefully
    const promises = serverIds.map(async (id) => {
      try {
        const resources = await this.getServerResources(id)
        return { id, resources, success: true }
      } catch (error) {
        return { id, resources: null, success: false, error }
      }
    })
    
    const results = await Promise.allSettled(promises)
    const resourceMap: Record<string, any> = {}
    
    results.forEach((result) => {
      if (result.status === 'fulfilled' && result.value.success) {
        resourceMap[result.value.id] = result.value.resources
      }
    })
    
    return resourceMap
  }

  // Database Server Management APIs (for MariaDB/MySQL/PostgreSQL servers)
  async getDatabaseServerDatabases(uuid: string): Promise<any[]> {
    // For database servers, get the databases that exist on this database server
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null
    const config = token ? { headers: { Authorization: `Bearer ${token}` }, validateStatus: (s: number) => s < 500 } : { validateStatus: (s: number) => s < 500 }
    const response = await this.client.get(`/hoard-api/servers/${uuid}/databases`, config)
    return response.data?.data || response.data || []
  }

  async getDatabaseServerUsers(uuid: string): Promise<any[]> {
    // For database servers, get the database users that exist on this database server
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null
    const config = token ? { headers: { Authorization: `Bearer ${token}` }, validateStatus: (s: number) => s < 500 } : { validateStatus: (s: number) => s < 500 }
    const response = await this.client.get(`/hoard-api/servers/${uuid}/database-users`, config)
    return response.data?.data || response.data || []
  }

  async getDatabaseServerAssociations(uuid: string): Promise<any[]> {
    // For database servers, get the game servers that are associated with this database server
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null
    const config = token ? { headers: { Authorization: `Bearer ${token}` }, validateStatus: (s: number) => s < 500 } : { validateStatus: (s: number) => s < 500 }
    const response = await this.client.get(`/hoard-api/servers/${uuid}/database-associations`, config)
    const result = response.data?.data || response.data || []
    apiLogger.debug('getDatabaseServerAssociations response:', { response: response.data, result, isArray: Array.isArray(result) })
    return result
  }

  async deleteDatabaseServerAssociation(uuid: string, associationId: string): Promise<void> {
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null
    const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {}
    await this.client.delete(`/hoard-api/servers/${uuid}/database-associations/${associationId}`, config)
  }

  async createDatabaseServerAssociation(uuid: string, associationData: any): Promise<any> {
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null
    const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {}
    const response = await this.client.post(`/hoard-api/servers/${uuid}/database-associations`, associationData, config)
    return response.data?.data || response.data
  }

  async createDatabaseServerDatabase(uuid: string, databaseData: any): Promise<any> {
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null
    const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {}
    const response = await this.client.post(`/hoard-api/servers/${uuid}/databases`, databaseData, config)
    return response.data?.data || response.data
  }

  async deleteDatabaseServerDatabase(uuid: string, databaseId: string): Promise<void> {
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null
    const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {}
    await this.client.delete(`/hoard-api/servers/${uuid}/databases/${databaseId}`, config)
  }

  async rotateDatabaseServerPassword(uuid: string, databaseId: string): Promise<any> {
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null
    const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {}
    const response = await this.client.post(`/hoard-api/servers/${uuid}/databases/${databaseId}/rotate-password`, {}, config)
    return response.data?.data || response.data
  }

  async createDatabaseServerUser(uuid: string, userData: any): Promise<any> {
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null
    const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {}
    const response = await this.client.post(`/hoard-api/servers/${uuid}/database-users`, userData, config)
    return response.data?.data || response.data
  }

  async deleteDatabaseServerUser(uuid: string, userId: string): Promise<void> {
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null
    const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {}
    await this.client.delete(`/hoard-api/servers/${uuid}/database-users/${userId}`, config)
  }

  // Database Server Credentials Management
  async getDatabaseServerCredentials(uuid: string): Promise<any> {
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null
    const config = token ? { headers: { Authorization: `Bearer ${token}` }, validateStatus: (s: number) => s < 500 } : { validateStatus: (s: number) => s < 500 }
    const response = await this.client.get(`/hoard-api/servers/${uuid}/credentials`, config)
    return response.data?.data || response.data || {}
  }

  async verifyDatabaseServerCredentials(uuid: string): Promise<{
    connection_status: 'connected' | 'failed' | 'not_configured'
    configured: boolean
    admin_username?: string
    host?: string
    port?: number
    database_type?: string
    error?: string
    message?: string
  }> {
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null
    const config = token ? { headers: { Authorization: `Bearer ${token}` }, validateStatus: (s: number) => s < 500 } : { validateStatus: (s: number) => s < 500 }
    const response = await this.client.get(`/hoard-api/servers/${uuid}/credentials/verify`, config)
    return response.data?.data || response.data || { connection_status: 'not_configured', configured: false }
  }

  async saveDatabaseServerCredentials(uuid: string, credentials: any): Promise<any> {
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null
    const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {}
    const response = await this.client.post(`/hoard-api/servers/${uuid}/credentials`, credentials, config)
    return response.data?.data || response.data
  }

  async deleteDatabaseServerCredentials(uuid: string): Promise<void> {
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null
    const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {}
    await this.client.delete(`/hoard-api/servers/${uuid}/credentials`, config)
  }

  // Cancel server subscription
  async cancelServerSubscription(uuid: string, disposition: OwnerDeleteDisposition): Promise<ServerCancellationReceipt> {
    const fullUuid = await this.resolveServerUuid(uuid)
    const response = await billingApi.delete<unknown>(`/hoard-api/subscription/cancel/${fullUuid}`, { data: disposition })
    const receipt = requireServerCancellationReceipt(response.data, fullUuid)
    this.serversCache = null
    return receipt
  }

  // Billing API methods
  // Lightweight HTTP passthroughs used by some services (e.g., RBAC)
  public async get(url: string, config?: any): Promise<any> { return (await this.client.get(url, config)).data }
  public async post(url: string, data?: any, config?: any): Promise<any> { return (await this.client.post(url, data, config)).data }
  public async put(url: string, data?: any, config?: any): Promise<any> { return (await this.client.put(url, data, config)).data }
  public async delete(url: string, config?: any): Promise<any> { return (await this.client.delete(url, config)).data }
  // Helper method for Hoard API calls with clean JWT auth
  private async hoardApiRequest(
    method: 'get' | 'post' | 'put' | 'patch' | 'delete',
    url: string,
    data?: any,
    options?: { timeoutMs?: number }
  ): Promise<any> {
    let token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null

    // If no access token, attempt a silent refresh before giving up
    if (!token) {
      const refreshed = await this.refreshHoardToken()
      if (refreshed) {
        token = localStorage.getItem('auth_token')
      }
    }

    // Skip the request entirely if still unauthenticated — avoids console 401 noise
    if (!token) {
      return Promise.reject(new Error('No Hoard auth token available'))
    }

    const config: any = {
      method,
      url,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      withCredentials: false,
      timeout: options?.timeoutMs ?? 10000
    }
    
    if (data && (method === 'post' || method === 'put')) {
      config.data = data
    }

    try {
      return await axios(config)
    } catch (err: any) {
      // On 401, try one refresh cycle then retry
      if (err?.response?.status === 401) {
        const refreshed = await this.refreshHoardToken()
        if (refreshed) {
          config.headers.Authorization = `Bearer ${localStorage.getItem('auth_token')}`
          return axios(config)
        }
      }
      throw err
    }
  }

  async getBillingData(): Promise<any> {
    const response = await this.hoardApiRequest('get', '/hoard-api/billing/')
    return response.data
  }

  async getBillingTimeseries(days: number = 31): Promise<Array<{ date: string; amount: number }>> {
    const response = await this.hoardApiRequest('get', `/hoard-api/billing/timeseries?days=${encodeURIComponent(String(days))}`)
    return response.data?.data || []
  }

  async getBillingEstimate(): Promise<BillingEstimate> {
    const session = localStorage.getItem('auth_token')
    if (this.billingEstimateRequest?.session === session) return this.billingEstimateRequest.promise
    const promise = this.hoardApiRequest('get', '/hoard-api/billing/estimate').then(response => {
      if (response.data?.error) throw new Error('Current billing estimate is unavailable')
      return response.data as BillingEstimate
    }).finally(() => {
      if (this.billingEstimateRequest?.promise === promise) this.billingEstimateRequest = null
    })
    this.billingEstimateRequest = { session, promise }
    return promise
  }

  async getBillingCredits(): Promise<{ available_credits?: number; available_credits_decimal?: number; month?: string; error?: string }> {
    try {
      const response = await this.hoardApiRequest('get', '/hoard-api/billing/credits', undefined, { timeoutMs: 20000 })
      if (response.data?.error) throw new Error('Available billing credit is unavailable')
      return response.data
    } catch (err: any) {
      apiLogger.error('Failed to load billing credits', {
        message: err?.message,
        code: err?.code,
        status: err?.response?.status,
        response: err?.response?.data,
      })
      throw err
    }
  }

  async getBillingHistory(): Promise<{ data: Array<{ month: string; total: number; by_service: Record<string, number>; is_estimate?: boolean }> }> {
    const response = await this.hoardApiRequest('get', '/hoard-api/billing/history')
    return response.data
  }

  async getServerBilling(serverUuid: string): Promise<ServerBillingData> {
    const response = await this.hoardApiRequest('get', `/hoard-api/billing/server/${serverUuid}`)
    return response.data
  }

  /**
   * Resolve a server reference (short identifier vs full UUID) to the full UUID.
   * - If it already looks like a UUID (contains dashes, length >= 32), returns as-is.
   * - Otherwise, searches the cached server list (or fetches if needed) by identifier.
   */
  private async resolveServerUuid(idOrUuid: string): Promise<string> {
    try {
      if (!idOrUuid) return idOrUuid
      const s = String(idOrUuid)
      // Already a UUID format
      if (s.includes('-') || s.length >= 32) return s

      // Try to find in cached servers list
      const servers = await this.getServers().catch(() => [])
      const match = Array.isArray(servers) ? servers.find((sv: any) => sv?.identifier === s || sv?.uuid?.startsWith(s)) : null
      if (match?.uuid) return match.uuid
      return s
    } catch {
      return idOrUuid
    }
  }

  async getInvoices(): Promise<any> {
    const response = await this.hoardApiRequest('get', '/hoard-api/invoice/')
    return response.data?.invoices || response.data || []
  }

  async getInvoiceDetails(invoiceId: string): Promise<any> {
    const response = await this.hoardApiRequest('get', `/hoard-api/invoice/${invoiceId}/details`)
    return response.data?.invoice || response.data
  }

  // Payment Methods API methods
  async getPaymentMethods(): Promise<any> {
    const response = await this.hoardApiRequest('get', '/hoard-api/billing/payment-methods')
    return response.data?.payment_methods || []
  }

  async createAddPaymentMethodIntent(): Promise<{ client_secret: string }> {
    const response = await this.hoardApiRequest('post', '/hoard-api/billing/add-payment-method-intent')
    return response.data
  }

  async getPackageRenewalOffers(): Promise<PackageRenewalOffer[]> {
    const response = await this.hoardApiRequest('get', '/hoard-api/billing/package-renewal-offers')
    return response.data?.data || []
  }

  async addPaymentMethod(paymentMethodId: string, packageRenewalAcceptances: Array<Record<string, unknown>> = []): Promise<unknown> {
    const response = await this.hoardApiRequest('post', '/hoard-api/billing/payment-methods', {
      payment_method_id: paymentMethodId,
      package_renewal_acceptances: packageRenewalAcceptances,
    })
    return response.data?.payment_method
  }

  async retryFailedPayment(): Promise<{ success: boolean; invoice_id: string; status: string; hosted_invoice_url?: string }> {
    const response = await this.hoardApiRequest('post', '/hoard-api/billing/retry-payment')
    return response.data
  }

  async removePaymentMethod(paymentMethodId: string): Promise<any> {
    const response = await this.hoardApiRequest('delete', `/hoard-api/billing/payment-methods/${paymentMethodId}`)
    return response.data
  }

  async setDefaultPaymentMethod(paymentMethodId: string): Promise<any> {
    const response = await this.hoardApiRequest('put', `/hoard-api/billing/payment-methods/${paymentMethodId}/default`)
    return response.data
  }

  // Billing Address API methods
  async getBillingAddress(): Promise<any> {
    const response = await this.hoardApiRequest('get', '/hoard-api/billing/address')
    return response.data?.address || null
  }

  async updateBillingAddress(address: any): Promise<any> {
    const response = await this.hoardApiRequest('put', '/hoard-api/billing/address', {
      address
    })
    return response.data
  }

  // Email API methods
  /**
   * Send password reset email via Hoard email API
   * Non-blocking: logs errors but doesn't throw to avoid disrupting password reset flow
   */
  async sendPasswordResetEmail(email: string, username: string, token: string, resetUrl: string): Promise<void> {
    try {
      await this.hoardApiRequest('post', '/hoard-api/email/send/password-reset', {
        email,
        username,
        token,
        reset_url: resetUrl
      })
    } catch (error) {
      apiLogger.error('Failed to send password reset email:', error)
      // Don't throw - email failure shouldn't block the password reset flow
    }
  }

  /**
   * Send account created email via Hoard email API
   * Non-blocking: logs errors but doesn't throw
   */
  async sendAccountCreatedEmail(email: string, username: string): Promise<void> {
    try {
      await this.hoardApiRequest('post', '/hoard-api/email/send/account-created', {
        email,
        username
      })
    } catch (error) {
      apiLogger.error('Failed to send account created email:', error)
    }
  }

  /**
   * Send subuser added notification via Hoard email API
   * Non-blocking: logs errors but doesn't throw
   */
  async sendSubuserAddedEmail(
    email: string,
    username: string,
    serverName: string,
    serverUuid: string,
    ownerUsername: string,
    permissions: string[]
  ): Promise<void> {
    try {
      await this.hoardApiRequest('post', '/hoard-api/email/send/subuser-added', {
        email,
        username,
        server_name: serverName,
        server_uuid: serverUuid,
        owner_username: ownerUsername,
        permissions
      })
    } catch (error) {
      apiLogger.error('Failed to send subuser added email:', error)
    }
  }

  /**
   * Send subuser removed notification via Hoard email API
   * Non-blocking: logs errors but doesn't throw
   */
  async sendSubuserRemovedEmail(
    email: string,
    username: string,
    serverName: string,
    ownerUsername: string
  ): Promise<void> {
    try {
      await this.hoardApiRequest('post', '/hoard-api/email/send/subuser-removed', {
        email,
        username,
        server_name: serverName,
        owner_username: ownerUsername
      })
    } catch (error) {
      apiLogger.error('Failed to send subuser removed email:', error)
    }
  }

  /**
   * Send server installed notification via Hoard email API
   * Non-blocking: logs errors but doesn't throw
   */
  async sendServerInstalledEmail(
    email: string,
    username: string,
    serverName: string,
    serverUuid: string
  ): Promise<void> {
    try {
      await this.hoardApiRequest('post', '/hoard-api/email/send/server-installed', {
        email,
        username,
        server_name: serverName,
        server_uuid: serverUuid
      })
    } catch (error) {
      apiLogger.error('Failed to send server installed email:', error)
    }
  }

  // Server State API methods (for restore/transfer progress, etc.)
  /**
   * Get temporary server state from Hoard (e.g., restore or transfer status)
   */
  async getServerState(serverUuid: string): Promise<{
    success: boolean
    data: {
      type: string
      serverUuid: string
      // Restore-specific fields
      backupUuid?: string
      backupName?: string
      backupBytes?: number
      // Transfer-specific fields
      sourceLocation?: string
      targetLocation?: string
      // Common fields
      startTime: string
    } | null
    created_at?: string
    expires_at?: string
  }> {
    const response = await this.hoardApiRequest('get', `/hoard-api/server-state/${serverUuid}`)
    return response.data
  }

  /**
   * Set temporary server state in Hoard with expiration
   */
  async setServerState(
    serverUuid: string,
    state: {
      type: string
      serverUuid: string
      // Restore-specific fields
      backupUuid?: string
      backupName?: string
      backupBytes?: number
      // Transfer-specific fields
      sourceLocation?: string
      targetLocation?: string
      // Common fields
      startTime: string
    },
    expiresInHours: number = 6
  ): Promise<{ success: boolean; message: string; expires_at: string }> {
    const response = await this.hoardApiRequest('post', `/hoard-api/server-state/${serverUuid}`, {
      state,
      expires_in_hours: expiresInHours
    })
    return response.data
  }

  /**
   * Delete server state from Hoard (e.g., when restore completes)
   */
  async deleteServerState(serverUuid: string): Promise<{ success: boolean; message: string }> {
    const response = await this.hoardApiRequest('delete', `/hoard-api/server-state/${serverUuid}`)
    return response.data
  }

  // ==================== Plugin Metadata API ====================
  // Server plugin tracking via Hoard database
  
  /**
   * Get all tracked plugins for a server
   */
  async getServerPluginsMeta(serverUuid: string): Promise<{
    modrinth_id: string
    slug: string
    name: string
    jar_filename: string
    version: string
    installed_at: string
    updated_at: string
    metadata?: any
  }[]> {
    const response = await this.hoardApiRequest('get', `/hoard-api/servers/${serverUuid}/plugins`)
    return response.data?.data || []
  }

  /**
   * Add or update a tracked plugin for a server
   */
  async addServerPluginMeta(serverUuid: string, pluginData: {
    modrinthId: string
    slug?: string
    name: string
    jarFilename: string
    version?: string
    metadata?: any
  }): Promise<any> {
    const response = await this.hoardApiRequest('post', `/hoard-api/servers/${serverUuid}/plugins`, {
      modrinth_id: pluginData.modrinthId,
      slug: pluginData.slug,
      name: pluginData.name,
      jar_filename: pluginData.jarFilename,
      version: pluginData.version,
      metadata: pluginData.metadata
    })
    return response.data?.data || response.data
  }

  /**
   * Remove a tracked plugin from a server
   */
  async removeServerPluginMeta(serverUuid: string, modrinthId: string): Promise<boolean> {
    try {
      await this.hoardApiRequest('delete', `/hoard-api/servers/${serverUuid}/plugins/${modrinthId}`)
      return true
    } catch (error: any) {
      if (error.response?.status === 404) {
        return false
      }
      throw error
    }
  }

  /**
   * Sync plugin records with actual jar files on the server.
   * Removes any tracked plugins whose jar files no longer exist.
   */
  async syncServerPluginsMeta(serverUuid: string, jarFiles: string[]): Promise<{
    removed: number
    plugins: {
      modrinth_id: string
      slug: string
      name: string
      jar_filename: string
      version: string
      installed_at: string
      updated_at: string
      metadata?: any
    }[]
  }> {
    const response = await this.hoardApiRequest('post', `/hoard-api/servers/${serverUuid}/plugins/sync`, {
      jarFiles
    })
    return response.data?.data || { removed: 0, plugins: [] }
  }

  /**
   * Log a config file edit activity.
   * Call this after saving a config file via the config editor
   * to create a distinct activity log entry.
   */
  async logConfigEdit(serverUuid: string, configFile: string, format?: string, pluginName?: string): Promise<void> {
    try {
      await this.hoardApiRequest('post', `/hoard-api/servers/${serverUuid}/activity/config-edit`, {
        config_file: configFile,
        format: format,
        plugin_name: pluginName
      })
    } catch (error) {
      // Don't throw - activity logging is non-critical
      apiLogger.warn('Failed to log config edit activity:', error)
    }
  }

  // ==================== RBAC / Permissions ====================

  /**
   * Get the current user's permissions for a specific server.
   * Returns both Panel built-in permissions and custom Metrly permissions.
   */
  async getServerPermissions(serverUuid: string): Promise<{ permissions: string[], is_owner: boolean }> {
    try {
      const response = await this.hoardApiRequest('get', `/servers/${serverUuid}/permissions`)
      return response.data?.data || { permissions: ['*'], is_owner: true }
    } catch (error) {
      // Fallback to owner permissions if endpoint fails (backward compat)
      apiLogger.warn('Failed to fetch server permissions:', error)
      return { permissions: ['*'], is_owner: true }
    }
  }

  /**
   * Get all custom Metrly permission definitions.
   * Used to populate the subuser permissions modal.
   */
  async getCustomPermissions(): Promise<{
    data: Array<{ key: string; name: string; description: string; category: string }>
    categories: Record<string, { name: string; description: string; icon?: string }>
  }> {
    try {
      const response = await this.hoardApiRequest('get', '/permissions/custom')
      return response.data || { data: [], categories: {} }
    } catch (error) {
      apiLogger.warn('Failed to fetch custom permissions:', error)
      return { data: [], categories: {} }
    }
  }

  /**
   * Update a subuser's permissions for a server.
   * This writes directly to Panel's database to include custom Metrly permissions.
   */
  async updateSubuserPermissions(serverUuid: string, userId: number, permissions: string[]): Promise<{ permissions: string[] }> {
    const response = await this.hoardApiRequest('patch', `/servers/${serverUuid}/subusers/${userId}/permissions`, {
      permissions
    })
    return response.data?.data || { permissions }
  }

  /**
   * Check if the current user has a specific permission on a server.
   */
  async checkPermission(serverUuid: string, permission: string): Promise<boolean> {
    try {
      const response = await this.hoardApiRequest('post', `/servers/${serverUuid}/check-permission`, {
        permission
      })
      return response.data?.allowed || false
    } catch {
      // Fail open for backward compatibility
      return true
    }
  }

  // ==================== Steam GSLT Management ====================

  /**
   * Link a Steam Web API key to the current user's account.
   */
  async linkSteamAccount(apiKey: string): Promise<{ success: boolean; steam_id: string }> {
    const response = await this.hoardApiRequest('post', '/hoard-api/account/steam', { api_key: apiKey })
    return response.data
  }

  /**
   * Get the current user's Steam account link status.
   */
  async getSteamAccountStatus(): Promise<{
    linked: boolean
    steam_id?: string
    is_valid?: boolean
    last_validated_at?: string
    token_count?: number
  }> {
    const response = await this.hoardApiRequest('get', '/hoard-api/account/steam')
    return response.data
  }

  /**
   * Unlink the Steam API key (revokes all GSLTs first).
   */
  async unlinkSteamAccount(): Promise<{ success: boolean; tokens_revoked: number }> {
    const response = await this.hoardApiRequest('delete', '/hoard-api/account/steam')
    return response.data
  }

  /**
   * List all GSLTs across all servers for this user.
   */
  async getSteamTokens(): Promise<{
    tokens: Array<{
      server_uuid: string
      server_name: string
      server_exists: boolean
      steam_id: string
      app_id: number
      memo: string | null
      is_expired: boolean
      is_banned: boolean
      last_checked_at: string | null
      created_at: string | null
    }>
  }> {
    const response = await this.hoardApiRequest('get', '/hoard-api/account/steam/tokens')
    return response.data
  }

  /**
   * Revoke and delete one GSLT owned by the current user without unlinking Steam.
   */
  async deleteSteamToken(serverUuid: string): Promise<{
    success: boolean
    server_uuid: string
    steam_revoked: boolean
    server_cleared: boolean
  }> {
    const response = await this.hoardApiRequest(
      'delete',
      `/hoard-api/account/steam/tokens/${encodeURIComponent(serverUuid)}`,
    )
    return response.data
  }

  /**
   * Get GSLT status for a specific server.
   */
  async getServerGslt(serverUuid: string): Promise<{
    has_gslt: boolean
    app_id?: number
    memo?: string
    login_token_masked?: string
    is_expired?: boolean
    is_banned?: boolean
    last_checked_at?: string
    created_at?: string
  }> {
    const response = await this.hoardApiRequest('get', `/hoard-api/servers/${serverUuid}/gslt`)
    return response.data
  }

  /**
   * Create a GSLT for a server (auto-detects app ID from server egg/type).
   */
  async createServerGslt(serverUuid: string, memo?: string): Promise<{
    success: boolean
    login_token_masked: string
    app_id: number
    memo: string
  }> {
    const response = await this.hoardApiRequest('post', `/hoard-api/servers/${serverUuid}/gslt`, {
      ...(memo ? { memo } : {})
    })
    return response.data
  }

  /**
   * Revoke and delete the GSLT for a server.
   */
  async deleteServerGslt(serverUuid: string): Promise<{ success: boolean }> {
    const response = await this.hoardApiRequest('delete', `/hoard-api/servers/${serverUuid}/gslt`)
    return response.data
  }

  /**
   * Reset (regenerate) the GSLT for a server.
   */
  async resetServerGslt(serverUuid: string): Promise<{
    success: boolean
    login_token_masked: string
  }> {
    const response = await this.hoardApiRequest('post', `/hoard-api/servers/${serverUuid}/gslt/reset`)
    return response.data
  }

  /**
   * Generate a pre-deploy (wizard) GSLT without an existing server UUID.
   */
  async generatePendingGslt(appId: number = 730, serviceType?: string): Promise<{
    pending_id: string
    token: string
    steam_id: string
    app_id: number
  }> {
    const payload: Record<string, unknown> = { app_id: appId }
    if (serviceType) payload.service_type = serviceType
    const response = await this.hoardApiRequest('post', '/hoard-api/servers/gslt/pending', payload)
    return response.data
  }

  /**
   * Revoke and delete a pre-deploy (wizard) GSLT by its pending_id.
   */
  async revokePendingGslt(pendingId: string): Promise<{ success: boolean }> {
    const response = await this.hoardApiRequest('delete', `/hoard-api/servers/gslt/pending/${pendingId}`)
    return response.data
  }

  // ==================== Discord Bot Management ====================

  /**
   * Link a Discord bot token (and optional client secret) to the current user.
   */
  async linkDiscordAccount(botToken: string, clientSecret?: string): Promise<{
    success: boolean
    application_id: string
    bot_user_id: string
    bot_username: string
    has_client_secret: boolean
    has_public_key: boolean
  }> {
    const response = await this.hoardApiRequest('post', '/hoard-api/account/discord', {
      bot_token: botToken,
      ...(clientSecret ? { client_secret: clientSecret } : {}),
    })
    return response.data
  }

  /**
   * Get the current user's Discord bot link status (never exposes secrets).
   */
  async getDiscordAccountStatus(): Promise<{
    linked: boolean
    application_id?: string
    bot_user_id?: string
    bot_username?: string
    has_client_secret?: boolean
    has_public_key?: boolean
    is_valid?: boolean
    last_validated_at?: string
    created_at?: string
  }> {
    const response = await this.hoardApiRequest('get', '/hoard-api/account/discord')
    return response.data
  }

  /**
   * Unlink the Discord bot (clears injected env vars from all servers).
   */
  async unlinkDiscordAccount(): Promise<{ success: boolean; servers_cleared: number }> {
    const response = await this.hoardApiRequest('delete', '/hoard-api/account/discord')
    return response.data
  }

  /**
   * List all servers the user's Discord bot is injected into.
   */
  async getDiscordLinks(): Promise<{
    linked: boolean
    links: Array<{
      server_uuid: string
      server_name: string
      application_id: string
      bot_user_id: string
      memo: string
      last_checked_at: string
      created_at: string
    }>
  }> {
    const response = await this.hoardApiRequest('get', '/hoard-api/account/discord/links')
    return response.data
  }

  /**
   * Get Discord bot status for a specific server.
   */
  async getServerDiscord(serverUuid: string): Promise<{
    has_bot: boolean
    application_id?: string
    bot_user_id?: string
    bot_username?: string
    injected_vars?: string[]
    invite_url?: string
    created_at?: string
  } | ProjectZomboidDiscordStatus> {
    const response = await this.hoardApiRequest('get', `/hoard-api/servers/${serverUuid}/discord`)
    return response.data
  }

  /**
   * Apply the linked Discord bot credentials to a server.
   */
  async applyServerDiscord(serverUuid: string, payload?: ProjectZomboidDiscordRequest): Promise<{
    success: boolean
    application_id: string
    bot_user_id: string
    bot_username: string
    injected_vars: string[]
    invite_url: string
    restart_required: boolean
  } | { data: ProjectZomboidDiscordOperation }> {
    const response = await this.hoardApiRequest('post', `/hoard-api/servers/${serverUuid}/discord`, payload)
    return response.data
  }

  /**
   * Remove the Discord bot credentials from a server.
   */
  async removeServerDiscord(serverUuid: string): Promise<{ success: boolean } | { data: ProjectZomboidDiscordOperation }> {
    const response = await this.hoardApiRequest('delete', `/hoard-api/servers/${serverUuid}/discord`)
    return response.data
  }

  async getPalworldOverview(serverUuid: string): Promise<PalworldOverview> {
    const response = await this.hoardApiRequest('get', `/hoard-api/servers/${serverUuid}/palworld/overview`, undefined, { timeoutMs: 4500 })
    return response.data.data
  }

  async getPalworldConfiguration(serverUuid: string): Promise<PalworldConfiguration> {
    const response = await this.hoardApiRequest('get', `/hoard-api/servers/${serverUuid}/palworld/configuration`, undefined, { timeoutMs: 5000 })
    return response.data.data
  }

  async updatePalworldConfiguration(serverUuid: string, revision: number, settings: Record<string, unknown>): Promise<PalworldConfiguration> {
    const response = await this.hoardApiRequest('put', `/hoard-api/servers/${serverUuid}/palworld/configuration`, { revision, settings })
    return response.data.data
  }

  async runPalworldOperation(serverUuid: string, type: 'apply_configuration' | 'save' | 'start' | 'stop' | 'restart'): Promise<PalworldOperation> {
    const path = type === 'apply_configuration'
      ? `/hoard-api/servers/${serverUuid}/palworld/configuration/apply`
      : `/hoard-api/servers/${serverUuid}/palworld/operations`
    const response = await this.hoardApiRequest(
      'post',
      path,
      type === 'apply_configuration' ? {} : { type },
    )
    return response.data.data
  }

  async getPalworldOperations(serverUuid: string): Promise<PalworldOperation[]> {
    const response = await this.hoardApiRequest('get', `/hoard-api/servers/${serverUuid}/palworld/operations`, undefined, { timeoutMs: 5000 })
    return response.data.data
  }

  async previewPalworldImport(serverUuid: string, file: string): Promise<PalworldImportPreview> {
    const response = await this.hoardApiRequest('post', `/hoard-api/servers/${serverUuid}/palworld/imports/preview`, { file }, { timeoutMs: 45000 })
    return response.data.data
  }

  async installPalworldImport(serverUuid: string, input: { file: string; fingerprint: string; safety_backup_uuid: string }): Promise<PalworldOperation> {
    const response = await this.hoardApiRequest('post', `/hoard-api/servers/${serverUuid}/palworld/imports`, input, { timeoutMs: 15 * 60 * 1000 })
    return response.data.data
  }

  async getPalworldConversionCandidates(serverUuid: string): Promise<PalworldConversionCandidates> {
    const response = await this.hoardApiRequest('get', `/hoard-api/servers/${serverUuid}/palworld/imports/conversion-candidates`, undefined, { timeoutMs: 20000 })
    return response.data.data
  }

  async convertPalworldCoopHost(serverUuid: string, input: { target_guid: string; safety_backup_uuid: string }): Promise<PalworldOperation> {
    const response = await this.hoardApiRequest('post', `/hoard-api/servers/${serverUuid}/palworld/imports/convert-host`, input, { timeoutMs: 15 * 60 * 1000 })
    return response.data.data
  }

  async getProjectZomboidDiscordOptions(serverUuid: string): Promise<{
    capabilities: ProjectZomboidDiscordCapabilities
    guilds: DiscordChannelChoice[]
    configuration?: ProjectZomboidDiscordConfiguration | null
    invite_url: string
  }> {
    const response = await this.hoardApiRequest('get', `/hoard-api/servers/${serverUuid}/discord/options`)
    return response.data.data
  }

  async getProjectZomboidDiscordChannels(serverUuid: string, guildId: string): Promise<{
    guild: DiscordChannelChoice
    channels: DiscordChannelChoice[]
  }> {
    const response = await this.hoardApiRequest(
      'get',
      `/hoard-api/servers/${serverUuid}/discord/guilds/${encodeURIComponent(guildId)}/channels`,
    )
    return response.data.data
  }
}

export const apiClient = new ApiClient()
export default apiClient
